import { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, WMSTileLayer, Polygon, Polyline, Marker, Popup, useMap, useMapEvents, LayersControl, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AlertesParcelle } from '@/components/AlertesParcelle';
import { PlantAnalysisDialog } from '@/components/PlantAnalysisDialog';
import {
  Map,
  Layers,
  Plus,
  Minus,
  Droplets,
  Droplet,
  Zap,
  Fence,
  ArrowDown,
  Calculator,
  Eye,
  EyeOff,
  Edit,
  Pencil,
  Trash2,
  Upload,
  Download,
  Filter,
  RefreshCw,
  Loader2,
  MapPin,
  Ruler,
  Settings,
  ChevronRight,
  ChevronDown,
  Route,
  Truck,
  Tractor,
  CheckSquare,
  Square,
  X,
  Satellite,
  MapIcon,
  Users,
  Navigation,
  Radio,
  Search,
  AlertTriangle,
  AlertCircle,
  Check,
  Clock,
  Cloud,
  CloudRain,
  Sun,
  Wind,
  CheckCircle2,
  Bell,
  FileSignature,
  Mail,
  Smartphone,
  Info,
  Leaf,
  Sparkles,
  Mic,
  MicOff,
  MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import TracesTravaux from '@/components/TracesTravaux';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Mapping couleurs et icônes par type de culture
const CULTURE_STYLES = {
  // Vignes
  'vigne': { color: '#7c3aed', icon: '🍇' },
  'raisin': { color: '#7c3aed', icon: '🍇' },
  // Fruits à noyau
  'abricot': { color: '#f97316', icon: '🍑' },
  'pêche': { color: '#fb923c', icon: '🍑' },
  'cerise': { color: '#dc2626', icon: '🍒' },
  'prune': { color: '#7c3aed', icon: '🫐' },
  // Fruits à pépins
  'pomme': { color: '#22c55e', icon: '🍎' },
  'poire': { color: '#84cc16', icon: '🍐' },
  // Plantes aromatiques & médicinales
  'lavande': { color: '#8b5cf6', icon: '💜' },
  'lavandin': { color: '#a78bfa', icon: '💜' },
  'thym': { color: '#65a30d', icon: '🌿' },
  'romarin': { color: '#16a34a', icon: '🌿' },
  'sauge': { color: '#059669', icon: '🌿' },
  'menthe': { color: '#10b981', icon: '🌱' },
  'mélisse': { color: '#34d399', icon: '🌱' },
  'origan': { color: '#22c55e', icon: '🌿' },
  'livèche': { color: '#4ade80', icon: '🌿' },
  // Céréales
  'blé': { color: '#eab308', icon: '🌾' },
  'orge': { color: '#ca8a04', icon: '🌾' },
  'maïs': { color: '#facc15', icon: '🌽' },
  'tournesol': { color: '#fbbf24', icon: '🌻' },
  'colza': { color: '#fde047', icon: '🌼' },
  'sorgho': { color: '#d97706', icon: '🌾' },
  // Légumes
  'melon': { color: '#f59e0b', icon: '🍈' },
  'tomate': { color: '#ef4444', icon: '🍅' },
  'courgette': { color: '#84cc16', icon: '🥒' },
  'aubergine': { color: '#6b21a8', icon: '🍆' },
  'poivron': { color: '#dc2626', icon: '🌶️' },
  'salade': { color: '#4ade80', icon: '🥬' },
  'carotte': { color: '#ea580c', icon: '🥕' },
  'oignon': { color: '#fde68a', icon: '🧅' },
  'ail': { color: '#f5f5f4', icon: '🧄' },
  // Oléagineux
  'olive': { color: '#65a30d', icon: '🫒' },
  'noix': { color: '#92400e', icon: '🥜' },
  'amande': { color: '#d6b88e', icon: '🌰' },
  // Prairies & Fourrages
  'prairie': { color: '#86efac', icon: '🌾' },
  'luzerne': { color: '#22d3ee', icon: '🌿' },
  'foin': { color: '#a3e635', icon: '🌾' },
  // Autres
  'jachère': { color: '#d1d5db', icon: '🏜️' },
  'bâtiment': { color: '#6b7280', icon: '🏠' },
  'sne': { color: '#94a3b8', icon: '🌲' },
  'nan': { color: '#9ca3af', icon: '❓' },
  'default': { color: '#9ca3af', icon: '🌱' }
};

// Fonction pour obtenir le style d'une culture
const getCultureStyle = (cultureName) => {
  if (!cultureName) return CULTURE_STYLES.default;
  
  const lowerName = cultureName.toLowerCase();
  
  // Recherche exacte d'abord
  for (const [key, style] of Object.entries(CULTURE_STYLES)) {
    if (lowerName === key) return style;
  }
  
  // Recherche partielle
  for (const [key, style] of Object.entries(CULTURE_STYLES)) {
    if (lowerName.includes(key) || key.includes(lowerName)) return style;
  }
  
  return CULTURE_STYLES.default;
};

// ==================== CALCULETTE PRODUIT POUR OPÉRATEURS ====================
// Interface simple avec gros chiffres pour le terrain - IDENTIQUE à InterventionsPage
const CalculetteProduit = ({ produit, onClose, volumeBouillie }) => {
  const [volumeCuve, setVolumeCuve] = useState('1000'); // Volume pulvé en L
  const [volumeBouillieHa, setVolumeBouillieHa] = useState(volumeBouillie || '200'); // L/ha
  const [doseHa, setDoseHa] = useState(produit?.quantite || produit?.dose_recommandee || ''); // Dose produit L/ha
  
  // Calculs
  const volumeCuveNum = parseFloat(volumeCuve) || 0;
  const volumeBouillieNum = parseFloat(volumeBouillieHa) || 200;
  const doseNum = parseFloat(doseHa) || 0;
  
  // Hectares traitables avec cette cuve
  const hectaresTraitables = volumeBouillieNum > 0 ? (volumeCuveNum / volumeBouillieNum) : 0;
  
  // Quantité de produit à mettre dans la cuve
  const quantiteProduit = hectaresTraitables * doseNum;
  
  // Concentration en mL/L de bouillie (pour vérification)
  const concentrationMlL = volumeBouillieNum > 0 ? (doseNum * 1000 / volumeBouillieNum) : 0;
  
  return (
    <div className="fixed inset-0 z-[400] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-background rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Calculette Pulvé</h2>
              {produit?.nom && (
                <p className="text-sm opacity-90">{produit.nom}</p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-primary-foreground hover:bg-white/20">
            <X className="w-6 h-6" />
          </Button>
        </div>
        
        {/* Fiche produit si sélectionné */}
        {produit?.numero_amm && (
          <div className="bg-green-50 dark:bg-green-950/30 p-3 border-b">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-green-700 dark:text-green-400">{produit.nom}</p>
                <p className="text-green-600 dark:text-green-500 text-xs">AMM: {produit.numero_amm} {produit.firme && `• ${produit.firme}`}</p>
                {produit.dose_max && (
                  <p className="text-xs mt-1">Dose max homologuée: <span className="font-bold">{produit.dose_max} {produit.unite || 'L/ha'}</span></p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Entrées avec GROS chiffres */}
        <div className="p-4 space-y-4">
          {/* Volume cuve */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              🚜 Volume de ma cuve (litres)
            </Label>
            <div className="flex gap-2">
              {['400', '600', '1000', '1500', '2000', '3000'].map(vol => (
                <Button
                  key={vol}
                  variant={volumeCuve === vol ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVolumeCuve(vol)}
                  className="flex-1 text-lg font-bold"
                >
                  {vol}
                </Button>
              ))}
            </div>
            <Input
              type="number"
              value={volumeCuve}
              onChange={e => setVolumeCuve(e.target.value)}
              className="text-center text-3xl font-bold h-16"
              placeholder="1000"
            />
          </div>
          
          {/* Volume bouillie/ha */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              💧 Volume bouillie (L/ha)
            </Label>
            <div className="flex gap-2">
              {['100', '150', '200', '300', '400'].map(vol => (
                <Button
                  key={vol}
                  variant={volumeBouillieHa === vol ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVolumeBouillieHa(vol)}
                  className="flex-1"
                >
                  {vol}
                </Button>
              ))}
            </div>
            <Input
              type="number"
              value={volumeBouillieHa}
              onChange={e => setVolumeBouillieHa(e.target.value)}
              className="text-center text-2xl font-bold h-14"
              placeholder="200"
            />
          </div>
          
          {/* Dose produit/ha */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              🧪 Dose produit ({produit?.unite || 'L/ha'})
            </Label>
            {produit?.dose_recommandee && (
              <p className="text-xs text-muted-foreground">
                Recommandé: {produit.dose_recommandee} {produit.unite || 'L/ha'}
                {produit.dose_max && ` (max: ${produit.dose_max})`}
              </p>
            )}
            <Input
              type="number"
              step="0.1"
              value={doseHa}
              onChange={e => setDoseHa(e.target.value)}
              className={`text-center text-3xl font-bold h-16 ${
                produit?.dose_max && parseFloat(doseHa) > produit.dose_max 
                  ? 'border-red-500 bg-red-50 text-red-700' 
                  : ''
              }`}
              placeholder="2.5"
            />
            {produit?.dose_max && parseFloat(doseHa) > produit.dose_max && (
              <p className="text-red-600 text-sm font-semibold">⚠️ DOSE DÉPASSÉE !</p>
            )}
          </div>
        </div>
        
        {/* RÉSULTAT avec très gros chiffres */}
        <div className="bg-primary/10 p-6 border-t-4 border-primary">
          <div className="text-center space-y-4">
            {/* Hectares traitables */}
            <div>
              <p className="text-sm text-muted-foreground mb-1">Avec cette cuve je traite</p>
              <p className="text-4xl font-black text-primary">
                {hectaresTraitables.toFixed(1)} ha
              </p>
            </div>
            
            {/* Quantité produit - LE RÉSULTAT PRINCIPAL */}
            <div className="bg-primary text-primary-foreground rounded-xl p-4">
              <p className="text-sm opacity-90 mb-1">JE METS DANS LA CUVE</p>
              <p className="text-6xl font-black">
                {quantiteProduit.toFixed(1)}
              </p>
              <p className="text-2xl font-bold">{produit?.unite === 'kg/ha' ? 'kg' : 'litres'} de produit</p>
            </div>
            
            {/* Info concentration */}
            <p className="text-xs text-muted-foreground">
              Concentration: {concentrationMlL.toFixed(1)} mL/L de bouillie
            </p>
          </div>
        </div>
        
        {/* Boutons */}
        <div className="p-4 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Fermer
          </Button>
          <Button 
            className="flex-1 text-lg font-bold"
            onClick={() => {
              toast.success(`✅ ${quantiteProduit.toFixed(1)} L pour ${hectaresTraitables.toFixed(1)} ha`);
              onClose();
            }}
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            OK, c'est noté !
          </Button>
        </div>
      </div>
    </div>
  );
};

// Légende des cultures avec icônes et couleurs distinctes
const CultureLegend = ({ stats, cultures, onFilterClick }) => {
  const [collapsed, setCollapsed] = useState(true); // Collapsed by default on mobile
  
  if (!stats?.par_culture) return null;
  
  return (
    <div className="absolute left-2 top-2 md:left-4 md:top-4 z-[1000] bg-background/95 backdrop-blur rounded-lg shadow-lg max-w-[180px] md:max-w-[220px]">
      <button 
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted rounded-lg md:rounded-b-none"
      >
        <h4 className="font-semibold text-xs md:text-sm flex items-center gap-2">
          <span>Assolement</span>
          <Badge variant="secondary" className="text-[10px] md:text-xs">{stats.total_surface} ha</Badge>
        </h4>
        <ChevronRight className={cn("w-4 h-4 transition-transform", !collapsed && "rotate-90")} />
      </button>
      {!collapsed && (
        <div className="px-3 pb-2">
          <ScrollArea className="max-h-[200px] md:max-h-[300px]">
            <div className="space-y-1">
              {Object.entries(stats.par_culture).map(([id, data]) => {
                const style = getCultureStyle(data.nom);
                return (
                  <button
                    key={id}
                    onClick={() => onFilterClick(id)}
                    className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-muted transition-colors text-left"
                  >
                    <div 
                      className="w-4 h-4 md:w-5 md:h-5 rounded flex items-center justify-center flex-shrink-0 text-[10px] md:text-xs" 
                      style={{ backgroundColor: style.color }}
                    >
                      {style.icon}
                    </div>
                    <span className="text-[10px] md:text-xs flex-1 truncate">{data.nom}</span>
                    <span className="text-[10px] md:text-xs text-muted-foreground font-medium">{data.surface?.toFixed(1)}</span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

// Légende des réseaux - Version compacte
const ReseauLegend = ({ visible, onToggle, collapsed, setCollapsed }) => {
  const types = [
    { id: 'irrigation', icon: Droplets, label: 'Irrig.', color: '#3b82f6' },
    { id: 'electricite', icon: Zap, label: 'Élec.', color: '#eab308' },
    { id: 'drainage', icon: ArrowDown, label: 'Drain.', color: '#06b6d4' },
    { id: 'cloture', icon: Fence, label: 'Clôt.', color: '#78716c' },
  ];
  
  return (
    <div className="absolute right-4 top-4 z-[1000] bg-background/95 backdrop-blur rounded-lg shadow-lg">
      <button 
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-2 py-1.5 flex items-center justify-between text-xs font-semibold hover:bg-muted rounded-t-lg"
      >
        <span>Réseaux</span>
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronRight className="w-3 h-3 rotate-90" />}
      </button>
      {!collapsed && (
        <div className="px-2 pb-2 space-y-1">
          {types.map(type => {
            const Icon = type.icon;
            const isVisible = visible[type.id];
            return (
              <button
                key={type.id}
                onClick={() => onToggle(type.id)}
                className={cn(
                  "flex items-center gap-1 w-full px-1.5 py-0.5 rounded text-xs transition-colors",
                  isVisible ? "bg-muted" : "opacity-50 hover:opacity-100"
                )}
              >
                <Icon className="w-3 h-3" style={{ color: type.color }} />
                <span className="flex-1 text-left">{type.label}</span>
                {isVisible ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Panneau de détails parcelle - Panel latéral droit pour ne pas masquer les boutons
const ParcelleDetails = ({ parcelle, onClose, onEdit, onDelete, onCreateIntervention }) => {
  const [interventions, setInterventions] = useState([]);
  const [loadingInterventions, setLoadingInterventions] = useState(false);
  const [irrigationData, setIrrigationData] = useState(null);
  const [loadingIrrigation, setLoadingIrrigation] = useState(false);
  const [showAnalysePhoto, setShowAnalysePhoto] = useState(false);
  
  useEffect(() => {
    if (parcelle?.id) {
      loadInterventions();
      loadIrrigation();
    }
  }, [parcelle?.id]);
  
  const loadInterventions = async () => {
    setLoadingInterventions(true);
    try {
      const response = await api.get(`/interventions?parcelle_id=${parcelle.id}`);
      setInterventions(response.data?.interventions || []);
    } catch (error) {
      console.log('Pas d\'interventions');
      setInterventions([]);
    } finally {
      setLoadingInterventions(false);
    }
  };

  const loadIrrigation = async () => {
    setLoadingIrrigation(true);
    try {
      const response = await api.get(`/irrigation/parcelle/${parcelle.id}`);
      setIrrigationData(response.data);
    } catch (error) {
      console.log('Pas de données irrigation');
      setIrrigationData(null);
    } finally {
      setLoadingIrrigation(false);
    }
  };
  
  if (!parcelle) return null;
  
  return (
    <>
    <div className="absolute top-4 right-4 bottom-20 z-[1001] w-[380px] max-w-[calc(100vw-2rem)] bg-background/98 backdrop-blur-sm rounded-xl shadow-2xl border overflow-hidden flex flex-col" data-testid="parcelle-details-panel">
      {/* Header fixe */}
      <div className="p-4 border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg truncate">{parcelle.nom}</h3>
            <p className="text-sm text-muted-foreground truncate">{parcelle.code || parcelle.exploitation || parcelle.exploitation_nom || 'Sans code'}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} className="ml-2 h-8 w-8 p-0 rounded-full hover:bg-destructive/10">
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Boutons d'action principaux - toujours visibles */}
        <div className="flex gap-2 mt-3">
          <Button 
            size="sm" 
            variant="default" 
            onClick={() => onCreateIntervention && onCreateIntervention(parcelle)}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="btn-new-intervention-from-details"
          >
            <Plus className="w-4 h-4 mr-1" /> Intervention
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowAnalysePhoto(true)}
            className="px-3 bg-purple-50 hover:bg-purple-100 border-purple-300 text-purple-700"
            title="Analyser une photo de culture"
            data-testid="btn-analyze-photo"
          >
            <Sparkles className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(parcelle)} className="px-3">
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            className="px-3 text-destructive hover:bg-destructive/10"
            onClick={() => onDelete && onDelete(parcelle.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Lien vers fiche client si la parcelle est rattachée à un client ETA */}
        {(parcelle.client_id || parcelle.client_eta_id) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.location.href = `/clients/${parcelle.client_id || parcelle.client_eta_id}`}
            className="w-full mt-2 bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-700"
            data-testid="btn-voir-fiche-client"
          >
            <ChevronRight className="w-4 h-4 mr-1" /> Voir fiche client {parcelle.client_nom ? `· ${parcelle.client_nom}` : ''}
          </Button>
        )}
      </div>
      
      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        <Tabs defaultValue="description" className="w-full">
          <TabsList className="grid grid-cols-4 w-full mb-3">
            <TabsTrigger value="description" className="text-xs">Info</TabsTrigger>
            <TabsTrigger value="culture" className="text-xs">Culture</TabsTrigger>
            <TabsTrigger value="interventions" className="text-xs relative">
              Interv.
              {interventions.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                  {interventions.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reseaux" className="text-xs">
              Irrigation
              {irrigationData?.stats?.nb_reseaux > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                  {irrigationData.stats.nb_reseaux}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="description" className="mt-0">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Surface</p>
                  <p className="font-bold text-lg">{parcelle.surface_ha || parcelle.surface || 0} ha</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Ilot PAC</p>
                  <p className="font-medium">{parcelle.ilot_pac || parcelle.ilot || '-'}</p>
                </div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Commune</p>
                <p className="font-medium">{parcelle.commune || '-'}</p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="culture" className="mt-0">
            {(() => {
              const cultureName = parcelle.culture_nom || parcelle.culture || 'Non affecté';
              const style = getCultureStyle(cultureName);
              return (
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <div 
                    className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center text-2xl" 
                    style={{ backgroundColor: style.color }}
                  >
                    {style.icon}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{cultureName}</p>
                    <p className="text-sm text-muted-foreground truncate">{parcelle.variete || 'Variété non définie'}</p>
                  </div>
                </div>
              );
            })()}
          </TabsContent>
          
          <TabsContent value="interventions" className="mt-0">
            {loadingInterventions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : interventions.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground mb-3">Aucune intervention</p>
                <Button size="sm" onClick={() => onCreateIntervention && onCreateIntervention(parcelle)}>
                  <Plus className="w-4 h-4 mr-1" /> Créer
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {interventions.map((interv, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{interv.type_intervention || interv.operation}</p>
                      <p className="text-xs text-muted-foreground">{interv.date_debut || interv.date}</p>
                    </div>
                    <Badge variant={interv.statut === 'termine' ? 'default' : 'secondary'} className="ml-2 flex-shrink-0">
                      {interv.statut || 'Planifié'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="reseaux" className="mt-0">
            {loadingIrrigation ? (
              <div className="text-center py-6">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-500" />
              </div>
            ) : irrigationData?.reseaux?.length > 0 ? (
              <div className="space-y-3">
                {/* Stats irrigation */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Surface irriguée</p>
                    <p className="font-bold text-blue-600">{irrigationData.stats?.surface_irriguee_ha || 0} ha</p>
                  </div>
                  <div className="p-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Conso. totale</p>
                    <p className="font-bold text-cyan-600">{irrigationData.stats?.consommation_totale_m3 || 0} m³</p>
                  </div>
                </div>
                
                {/* Liste des réseaux */}
                <div className="space-y-2">
                  {irrigationData.reseaux.map(reseau => (
                    <div key={reseau.id} className="p-3 bg-muted/50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{reseau.nom}</span>
                        <Badge className={reseau.statut === 'operationnel' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {reseau.statut || 'planifié'}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {reseau.type_irrigation?.replace('_', ' ')} • {reseau.surface_couverte_ha} ha • {reseau.source_eau}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Sessions récentes */}
                {irrigationData.sessions_recentes?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium mb-2">Sessions récentes</p>
                    <div className="space-y-1">
                      {irrigationData.sessions_recentes.slice(0, 3).map(session => (
                        <div key={session.id} className="text-xs flex justify-between p-2 bg-muted/30 rounded">
                          <span>{new Date(session.date_debut).toLocaleDateString('fr-FR')}</span>
                          <span>{session.duree_minutes} min • {session.volume_m3 || '?'} m³</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Lien vers module irrigation */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={() => window.location.href = '/irrigation'}
                >
                  <Droplets className="w-4 h-4 mr-2" />
                  Gérer l'irrigation
                </Button>
              </div>
            ) : (
              <div className="text-center py-6">
                <Droplets className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">Aucun réseau d'irrigation</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => window.location.href = '/irrigation'}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un réseau
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
    
    {/* Dialog analyse photo IA */}
    <PlantAnalysisDialog
      parcelle={parcelle}
      culture={parcelle?.culture}
      trigger={null}
      onAnalysisComplete={(result) => {
        toast.success('Analyse terminée !');
        setShowAnalysePhoto(false);
      }}
      isOpen={showAnalysePhoto}
      onOpenChange={setShowAnalysePhoto}
    />
    </>
  );
};

// Calculateur de débit
const DebitCalculator = ({ isOpen, onClose }) => {
  const [params, setParams] = useState({
    longueur_m: 100,
    diametre_mm: 25,
    pression_bars: 3,
    materiau: 'pehd',
    type_irrigation: 'goutte_a_goutte'
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const calculate = async () => {
    setLoading(true);
    try {
      const response = await api.post('/parcellaire/calculs/debit', params);
      setResult(response.data);
    } catch (error) {
      toast.error('Erreur de calcul');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Calculateur de débit
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Longueur (m)</Label>
              <Input
                type="number"
                value={params.longueur_m}
                onChange={e => setParams({...params, longueur_m: parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <Label>Diamètre (mm)</Label>
              <Input
                type="number"
                value={params.diametre_mm}
                onChange={e => setParams({...params, diametre_mm: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Pression (bars)</Label>
              <Input
                type="number"
                step="0.5"
                value={params.pression_bars}
                onChange={e => setParams({...params, pression_bars: parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <Label>Matériau</Label>
              <Select value={params.materiau} onValueChange={v => setParams({...params, materiau: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pehd">PEHD</SelectItem>
                  <SelectItem value="pvc">PVC</SelectItem>
                  <SelectItem value="acier">Acier</SelectItem>
                  <SelectItem value="fonte">Fonte</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label>Type d'irrigation</Label>
            <Select value={params.type_irrigation} onValueChange={v => setParams({...params, type_irrigation: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="goutte_a_goutte">Goutte-à-goutte</SelectItem>
                <SelectItem value="aspersion">Aspersion</SelectItem>
                <SelectItem value="micro_aspersion">Micro-aspersion</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={calculate} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Calculer
          </Button>
          
          {result && (
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Débit</p>
                  <p className="font-bold text-lg">{result.resultats.debit_lh} L/h</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Vitesse</p>
                  <p className="font-bold text-lg">{result.resultats.vitesse_ms} m/s</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pertes de charge</p>
                  <p className="font-medium">{result.resultats.pertes_charge_bars} bars</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pression résiduelle</p>
                  <p className="font-medium">{result.resultats.pression_residuelle_bars} bars</p>
                </div>
              </div>
              {result.recommandations?.length > 0 && (
                <div className="border-t pt-2 mt-2">
                  {result.recommandations.map((r, i) => (
                    <p key={i} className="text-sm">{r}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Mode dessin sur la carte
const DrawingMode = ({ onDraw, mode, onModeChange, onCancel, selectingPoint, onPointSelected }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [points, setPoints] = useState([]);
  const [surfaceCalculee, setSurfaceCalculee] = useState(0);
  
  // Calcul de surface en temps réel (formule de Shoelace / Gauss)
  const calculerSurface = useCallback((pts) => {
    if (pts.length < 3) return 0;
    
    // Convertir lat/lng en mètres approximatifs
    const toMeters = (lat, lng, refLat, refLng) => {
      const R = 6371000; // Rayon Terre en mètres
      const dLat = (lat - refLat) * Math.PI / 180;
      const dLng = (lng - refLng) * Math.PI / 180;
      const x = dLng * Math.cos(refLat * Math.PI / 180) * R;
      const y = dLat * R;
      return { x, y };
    };
    
    const refLat = pts[0][0];
    const refLng = pts[0][1];
    const ptsMeters = pts.map(p => toMeters(p[0], p[1], refLat, refLng));
    
    // Formule de Shoelace
    let area = 0;
    for (let i = 0; i < ptsMeters.length; i++) {
      const j = (i + 1) % ptsMeters.length;
      area += ptsMeters[i].x * ptsMeters[j].y;
      area -= ptsMeters[j].x * ptsMeters[i].y;
    }
    area = Math.abs(area) / 2;
    
    // Convertir m² en hectares
    return (area / 10000).toFixed(2);
  }, []);
  
  // Activer automatiquement quand mode est défini
  useEffect(() => {
    if (mode && !isDrawing) {
      setIsDrawing(true);
      setPoints([]);
      setSurfaceCalculee(0);
    }
  }, [mode]);
  
  // Recalculer la surface à chaque nouveau point
  useEffect(() => {
    if (points.length >= 3 && mode === 'parcelle') {
      setSurfaceCalculee(calculerSurface(points));
    }
  }, [points, mode, calculerSurface]);
  
  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        // Gestion des clics pour l'itinéraire (points A/B)
        if (selectingPoint && onPointSelected) {
          onPointSelected(selectingPoint, { lat: e.latlng.lat, lng: e.latlng.lng });
          return;
        }
        
        // Gestion des clics pour le dessin
        if (!mode || !isDrawing) return;
        const newPoint = [e.latlng.lat, e.latlng.lng];
        setPoints(prev => [...prev, newPoint]);
      }
    });
    return null;
  };
  
  const finishDrawing = () => {
    const minPoints = mode === 'parcelle' ? 3 : 2;
    if (points.length < minPoints) {
      toast.error(`Au moins ${minPoints} points requis pour ${mode === 'parcelle' ? 'une parcelle' : 'un réseau'}`);
      return;
    }
    // Passer la surface calculée avec les points
    onDraw(points, mode, surfaceCalculee);
    setPoints([]);
    setIsDrawing(false);
    setSurfaceCalculee(0);
    onModeChange(null);
  };
  
  const cancelDrawing = () => {
    setPoints([]);
    setIsDrawing(false);
    setSurfaceCalculee(0);
    onModeChange(null);
    if (onCancel) onCancel();
  };
  
  const undoLastPoint = () => {
    setPoints(prev => prev.slice(0, -1));
  };
  
  if (!mode) return <MapClickHandler />;
  
  return (
    <>
      <MapClickHandler />
      
      {/* Barre d'outils de dessin en bas */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-background/95 backdrop-blur rounded-xl shadow-lg p-3 flex items-center gap-3">
        <div className="flex items-center gap-2 pr-3 border-r">
          {mode === 'parcelle' ? (
            <MapPin className="w-5 h-5 text-green-600" />
          ) : (
            <Ruler className="w-5 h-5 text-blue-600" />
          )}
          <span className="font-medium">
            {mode === 'parcelle' ? 'Dessiner parcelle' : 'Tracer réseau'}
          </span>
        </div>
        
        <Badge variant="secondary" className="px-3">
          {points.length} point{points.length > 1 ? 's' : ''}
        </Badge>
        
        {/* Affichage surface en temps réel */}
        {mode === 'parcelle' && points.length >= 3 && (
          <Badge variant="default" className="px-3 bg-green-600">
            📐 {surfaceCalculee} ha
          </Badge>
        )}
        
        {points.length > 0 && (
          <Button size="sm" variant="ghost" onClick={undoLastPoint}>
            Annuler dernier
          </Button>
        )}
        
        <Button 
          size="sm" 
          onClick={finishDrawing}
          disabled={points.length < (mode === 'parcelle' ? 3 : 2)}
          className={mode === 'parcelle' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
        >
          Terminer
        </Button>
        
        <Button size="sm" variant="destructive" onClick={cancelDrawing}>
          Annuler
        </Button>
      </div>
      
      {/* Instructions */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[999] pointer-events-none">
        {points.length === 0 && (
          <div className="bg-black/70 text-white px-4 py-2 rounded-lg text-sm">
            👆 Cliquez sur la carte pour placer des points
          </div>
        )}
      </div>
      
      {/* Visualisation du dessin en cours */}
      {points.length > 0 && mode === 'parcelle' && points.length >= 3 && (
        <Polygon 
          positions={points} 
          pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.3, weight: 2, dashArray: '5,5' }}
        />
      )}
      
      {points.length > 0 && (
        <Polyline 
          positions={points} 
          pathOptions={{ 
            color: mode === 'parcelle' ? '#22c55e' : '#3b82f6', 
            weight: 3, 
            dashArray: '5,5' 
          }}
        />
      )}
      
      {/* Marqueurs aux points - DRAGGABLE pour ajuster au doigt sur mobile/desktop */}
      {points.map((point, idx) => (
        <Marker 
          key={idx} 
          position={point}
          draggable={true}
          eventHandlers={{
            dragend: (e) => {
              const latlng = e.target.getLatLng();
              setPoints(prev => prev.map((p, i) => i === idx ? [latlng.lat, latlng.lng] : p));
            }
          }}
          icon={L.divIcon({
            className: 'custom-marker',
            html: `<div class="w-6 h-6 rounded-full ${mode === 'parcelle' ? 'bg-green-500' : 'bg-blue-500'} border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold cursor-move" style="touch-action:none;">${idx + 1}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          })}
          data-testid={`parcelle-point-marker-${idx}`}
        />
      ))}
    </>
  );
};

// Page principale
// ========================== BATIMENT PLACEMENT HANDLER ==========================
const BatimentPlacementHandler = ({ onPlace }) => {
  useMapEvents({
    click: (e) => {
      onPlace(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
};

export const ParcellairePage = () => {
  const [parcelles, setParcelles] = useState([]);
  const [reseaux, setReseaux] = useState([]);
  const [stats, setStats] = useState(null);
  const [cultures, setCultures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParcelle, setSelectedParcelle] = useState(null);
  const [campagne, setCampagne] = useState(2026);
  const [visibleReseaux, setVisibleReseaux] = useState({
    irrigation: true,
    electricite: true,
    drainage: true,
    cloture: true
  });
  const [showCalculator, setShowCalculator] = useState(false);
  const [drawMode, setDrawMode] = useState(null);
  const [mapCenter, setMapCenter] = useState([44.34, 5.28]); // Sainte-Jalle, Drôme
  const [mapZoom, setMapZoom] = useState(12);
  const [mapLayer, setMapLayer] = useState('satellite'); // 'osm', 'satellite', 'cadastre'
  const [reseauLegendCollapsed, setReseauLegendCollapsed] = useState(false);
  const [teamPositions, setTeamPositions] = useState([]);
  const [carteLive, setCarteLive] = useState({ chauffeurs: [], interventions: [] });
  const [showTeamTracking, setShowTeamTracking] = useState(true);
  const [showTracesTravaux, setShowTracesTravaux] = useState(false);
  const [trackerPositions, setTrackerPositions] = useState([]);
  const [myPosition, setMyPosition] = useState(null);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [locatingMe, setLocatingMe] = useState(false);
  const [showCreateFromPosition, setShowCreateFromPosition] = useState(false);
  const [createFromPositionMode, setCreateFromPositionMode] = useState(null); // 'parcelle' or 'client'
  const [newClientFromPosition, setNewClientFromPosition] = useState({ nom: '', contact: '', telephone: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCultures, setSelectedCultures] = useState([]); // Array of selected culture names
  const [selectedExploitations, setSelectedExploitations] = useState([]); // Array of selected exploitation ids
  const [exploitations, setExploitations] = useState([]);
  const [showCultureFilter, setShowCultureFilter] = useState(false);
  const [showExploitationFilter, setShowExploitationFilter] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQuickCreateExploitation, setShowQuickCreateExploitation] = useState(false);
  const [quickExploitation, setQuickExploitation] = useState({ nom: '', commune: '', siret: '', adresse: '' });
  const [searchingPappers, setSearchingPappers] = useState(false);
  const [pappersResults, setPappersResults] = useState([]);
  const [showCreateReseauDialog, setShowCreateReseauDialog] = useState(false);
  const [pendingGeometry, setPendingGeometry] = useState(null);
  const [pendingCenter, setPendingCenter] = useState(null);
  const [pendingSurface, setPendingSurface] = useState(0);
  const [pendingLongueur, setPendingLongueur] = useState(0);
  
  const [showItineraire, setShowItineraire] = useState(false);
  const [itineraireData, setItineraireData] = useState({
    pointA: null,
    pointB: null,
    pointALabel: '',
    pointBLabel: '',
    largeur: '3.5',
    hauteur: '4',
    poids: '40',
    typeVehicule: 'tracteur'
  });
  const [selectingPoint, setSelectingPoint] = useState(null); // 'A' or 'B'
  const [citySearchA, setCitySearchA] = useState('');
  const [citySearchB, setCitySearchB] = useState('');
  const [citySuggestionsA, setCitySuggestionsA] = useState([]);
  const [citySuggestionsB, setCitySuggestionsB] = useState([]);
  const [searchingCity, setSearchingCity] = useState(false);
  
  // Itinéraire calculé (polyline) et état de calcul
  const [calculatedRoute, setCalculatedRoute] = useState(null);
  const [calculatingRoute, setCalculatingRoute] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null); // { distance, duration, warnings }
  
  // Option pour éviter les autoroutes/voies rapides (tracteurs)
  const [eviterAutoroutes, setEviterAutoroutes] = useState(true);
  
  // === ASSISTANT VOCAL ITINÉRAIRE ===
  const [showVoiceCommand, setShowVoiceCommand] = useState(false);
  const [voiceCommand, setVoiceCommand] = useState('');

  // === BATIMENTS / INFRASTRUCTURES ===
  const [batiments, setBatiments] = useState([]);
  const [showCreateBatiment, setShowCreateBatiment] = useState(false);
  const [showBatiments, setShowBatiments] = useState(true);
  const [newBatiment, setNewBatiment] = useState({
    nom: '', type_batiment: 'hangar', description: '', 
    latitude: null, longitude: null,
    nom_entreprise: '', accessible_a_tous: true, surface_m2: ''
  });
  const [placingBatiment, setPlacingBatiment] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceResponse, setVoiceResponse] = useState(null);
  const [processingVoice, setProcessingVoice] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  
  // Fonction pour faire parler l'IA
  const speakResponse = (text) => {
    if (!synthRef.current) return;
    
    // Arrêter toute parole en cours
    synthRef.current.cancel();
    
    // Nettoyer le texte (enlever les ** du markdown)
    const cleanText = text.replace(/\*\*/g, '').replace(/\*/g, '');
    
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.lang = 'fr-FR';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    
    // Chercher une voix française
    const voices = synthRef.current.getVoices();
    const frenchVoice = voices.find(v => v.lang.startsWith('fr')) || voices[0];
    if (frenchVoice) utterance.voice = frenchVoice;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    synthRef.current.speak(utterance);
  };
  
  // Initialiser la reconnaissance vocale
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'fr-FR';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setVoiceCommand(transcript);
        
        // Si résultat final, envoyer la commande
        if (event.results[0].isFinal) {
          setIsListening(false);
          handleVoiceCommand(transcript);
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Erreur reconnaissance vocale:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone non autorisé. Veuillez l\'activer dans les paramètres du navigateur.');
        }
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);
  
  // Démarrer/arrêter l'écoute vocale
  const toggleVoiceListening = () => {
    if (!recognitionRef.current) {
      toast.error('La reconnaissance vocale n\'est pas supportée par votre navigateur.');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setVoiceCommand('');
      setVoiceResponse(null);
      setIsListening(true);
      recognitionRef.current.start();
      toast.info('🎤 Je vous écoute... Dites par exemple "Emmène-moi à la parcelle des Oliviers"');
    }
  };
  
  // Traiter la commande vocale
  const handleVoiceCommand = async (command) => {
    if (!command.trim()) return;
    
    setProcessingVoice(true);
    try {
      const response = await api.post('/geo/voice-itineraire', {
        commande: command,
        position_actuelle: myPosition ? { lat: myPosition.latitude, lng: myPosition.longitude } : null
      });
      
      const data = response.data;
      setVoiceResponse(data);
      
      if (data.success && data.destination_coords) {
        // Lancer automatiquement l'itinéraire
        setItineraireData(prev => ({
          ...prev,
          pointA: myPosition ? { lat: myPosition.latitude, lng: myPosition.longitude } : prev.pointA,
          pointALabel: myPosition ? '📍 Ma position' : prev.pointALabel,
          pointB: data.destination_coords,
          pointBLabel: `🎯 ${data.destination_nom}`,
          typeVehicule: data.vehicule_agricole ? 'tracteur' : prev.typeVehicule
        }));
        setEviterAutoroutes(data.eviter_autoroutes);
        setShowItineraire(true);
        setShowVoiceCommand(false);
        
        // Faire parler l'IA
        speakResponse(data.message_ia);
        toast.success(data.message_ia);
        
        // Centrer la carte sur la destination
        if (data.destination_coords) {
          setMapCenter([data.destination_coords.lat, data.destination_coords.lng]);
        }
        
        // Déclencher le calcul d'itinéraire après un court délai
        setTimeout(() => {
          // Le calcul sera fait par l'effet ou manuellement
        }, 500);
      } else {
        // Faire parler l'IA même en cas d'échec
        speakResponse(data.message_ia);
        toast.info(data.message_ia);
      }
    } catch (error) {
      console.error('Erreur commande vocale:', error);
      toast.error('Erreur lors du traitement de la commande vocale');
    } finally {
      setProcessingVoice(false);
    }
  };
  
  // Fonction pour utiliser automatiquement ma position comme point de départ
  const useMyPositionAsStart = () => {
    if (myPosition) {
      setItineraireData(prev => ({
        ...prev,
        pointA: { lat: myPosition.latitude, lng: myPosition.longitude },
        pointALabel: '📍 Ma position'
      }));
      toast.success('Position actuelle utilisée comme départ');
    } else {
      // Si pas de position, essayer de la récupérer
      toast.info('Recherche de votre position...');
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };
            setMyPosition(pos);
            setItineraireData(prev => ({
              ...prev,
              pointA: { lat: pos.latitude, lng: pos.longitude },
              pointALabel: '📍 Ma position'
            }));
            setMapCenter([pos.latitude, pos.longitude]);
            toast.success(`Position trouvée (±${Math.round(pos.accuracy)}m)`);
          },
          (error) => {
            console.error('Erreur GPS:', error);
            if (error.code === 1) {
              toast.error("Accès GPS refusé. Autorisez la localisation dans les paramètres.", { duration: 5000 });
            } else if (error.code === 2) {
              toast.error("GPS indisponible. Activez-le ou allez dehors.", { duration: 5000 });
            } else {
              toast.error("Délai GPS dépassé. Réessayez dehors.", { duration: 5000 });
            }
          },
          { enableHighAccuracy: true, timeout: 25000, maximumAge: 30000 }
        );
      } else {
        toast.error("GPS non disponible sur cet appareil");
      }
    }
  };
  
  // Fonction pour calculer l'itinéraire poids lourds via OSRM (gratuit, pas de clé requise)
  // AMELIORE : Support tracteur avec option éviter autoroutes
  const calculateHGVRoute = async () => {
    if (!itineraireData.pointA || !itineraireData.pointB) {
      toast.error('Définissez les points de départ et d\'arrivée');
      return;
    }
    
    setCalculatingRoute(true);
    setCalculatedRoute(null);
    setRouteInfo(null);
    
    try {
      // Construire l'URL OSRM avec options
      // Pour éviter les autoroutes, on utilise exclude=motorway
      let osrmUrl = `https://router.project-osrm.org/route/v1/driving/${itineraireData.pointA.lng},${itineraireData.pointA.lat};${itineraireData.pointB.lng},${itineraireData.pointB.lat}?overview=full&geometries=geojson&steps=true&annotations=true`;
      
      // Note: OSRM public ne supporte pas exclude, mais on peut utiliser alternatives
      // Pour une vraie solution tracteur, il faudrait un serveur OSRM custom avec profil agricole
      if (eviterAutoroutes && itineraireData.typeVehicule === 'tracteur') {
        osrmUrl += '&alternatives=3'; // Demander des alternatives pour choisir la plus adaptée
      }
      
      const response = await fetch(osrmUrl);
      
      if (!response.ok) {
        throw new Error('Erreur OSRM');
      }
      
      const data = await response.json();
      
      if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
        let selectedRoute = data.routes[0];
        
        // Si on a des alternatives et qu'on est en tracteur, choisir la route la plus "lente"
        // car les routes rapides sont souvent des autoroutes
        if (eviterAutoroutes && itineraireData.typeVehicule === 'tracteur' && data.routes.length > 1) {
          // Analyser les routes pour éviter celles avec des segments à haute vitesse
          const routesAnalyzed = data.routes.map((route, idx) => {
            const avgSpeedKmh = (route.distance / route.duration) * 3.6;
            const hasHighSpeed = avgSpeedKmh > 70; // Si vitesse moyenne > 70km/h, probablement autoroute
            return { route, idx, avgSpeedKmh, hasHighSpeed };
          });
          
          // Préférer la route avec la vitesse moyenne la plus basse (évite autoroutes)
          const safestRoute = routesAnalyzed
            .filter(r => !r.hasHighSpeed || routesAnalyzed.every(x => x.hasHighSpeed))
            .sort((a, b) => a.avgSpeedKmh - b.avgSpeedKmh)[0];
          
          if (safestRoute) {
            selectedRoute = safestRoute.route;
          }
        }
        
        // Convertir les coordonnées [lng, lat] en [lat, lng] pour Leaflet
        const coords = selectedRoute.geometry.coordinates.map(c => [c[1], c[0]]);
        setCalculatedRoute(coords);
        
        const warnings = [];
        
        // Calculer la vitesse moyenne pour détecter les voies rapides
        const avgSpeedKmh = (selectedRoute.distance / selectedRoute.duration) * 3.6;
        
        // AVERTISSEMENT TRACTEUR - Voies rapides détectées
        if (itineraireData.typeVehicule === 'tracteur') {
          if (avgSpeedKmh > 80) {
            warnings.push('🚫 ATTENTION : Cet itinéraire passe probablement par une AUTOROUTE ou VOIE RAPIDE interdite aux tracteurs !');
            warnings.push('👉 Utilisez Google Maps ou Waze et sélectionnez "Éviter autoroutes" manuellement');
          } else if (avgSpeedKmh > 60) {
            warnings.push('⚠️ Certaines portions peuvent être des voies rapides. Vérifiez sur le terrain.');
          }
          warnings.push('🚜 Vitesse max tracteur : 25-40 km/h selon homologation');
        }
        
        // Avertissements basés sur les dimensions
        if (parseFloat(itineraireData.largeur) > 2.55) warnings.push('Largeur > 2.55m : vérifiez les passages étroits');
        if (parseFloat(itineraireData.largeur) > 3) warnings.push('Largeur > 3m : signalisation obligatoire');
        if (parseFloat(itineraireData.largeur) > 4) warnings.push('Largeur > 4m : véhicule d\'escorte peut être requis');
        if (parseFloat(itineraireData.hauteur) > 4) warnings.push('Hauteur > 4m : attention aux ponts et tunnels');
        if (parseFloat(itineraireData.poids) > 44) warnings.push('Poids > 44t : autorisation spéciale requise');
        
        // Estimation durée réaliste pour tracteur (25 km/h moyen)
        let durationMinutes = Math.round(selectedRoute.duration / 60);
        let estimatedDurationTractor = null;
        if (itineraireData.typeVehicule === 'tracteur') {
          estimatedDurationTractor = Math.round((selectedRoute.distance / 1000) / 25 * 60); // 25 km/h
          warnings.push(`⏱️ Durée estimée tracteur (25 km/h) : ~${estimatedDurationTractor} min`);
        }
        
        setRouteInfo({
          distance: (selectedRoute.distance / 1000).toFixed(1),
          duration: durationMinutes,
          durationTractor: estimatedDurationTractor,
          avgSpeed: Math.round(avgSpeedKmh),
          warnings
        });
        
        const durationDisplay = itineraireData.typeVehicule === 'tracteur' 
          ? `~${estimatedDurationTractor} min (tracteur)`
          : `~${durationMinutes} min`;
        
        toast.success(`Itinéraire : ${(selectedRoute.distance / 1000).toFixed(1)} km, ${durationDisplay}`);
      } else {
        throw new Error('Aucun itinéraire trouvé');
      }
    } catch (error) {
      console.error('Erreur calcul itinéraire:', error);
      toast.error('Erreur lors du calcul. Vérifiez les points A et B.');
    }
    
    setCalculatingRoute(false);
  };
  
  // Formulaire de création de parcelle
  const [newParcelle, setNewParcelle] = useState({
    nom: '',
    exploitation_id: '',
    culture_id: '',
    ilot_pac: '',
    commune: '',
    code_cadastral: '',
    variete: '',
    mode_culture: 'conventionnel', // conventionnel, bio, conversion, raisonne
    is_bio: false, // True si bio ou conversion bio
    certification_bio: '', // AB, Demeter, Nature & Progrès, etc.
    irrigation: false,
    notes: '',
    // Mode Prestataire
    client_id: '',
    client_nom: '',
    exploitation_client: ''
  });
  
  // État pour le mode prestataire
  const [modePrestataire, setModePrestataire] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  
  // Formulaire de création de réseau
  const [newReseau, setNewReseau] = useState({
    nom: '',
    type_reseau: 'irrigation',
    statut: 'prevu',
    diametre_mm: '',
    materiau: 'pehd',
    notes: '',
    // Nouveaux champs
    source_alimentation: '', // forage, prise_eau, bassin, cuve
    debit_m3h: '',
    pression_bar: '',
    type_distribution: '', // rampe, goutte_a_goutte, asperseur, micro_aspersion
    longueur_estimee_m: '',
    parcelle_id: '' // Parcelle associée
  });
  
  // Dialog création intervention
  const [showInterventionDialog, setShowInterventionDialog] = useState(false);
  const [showAlertesDialog, setShowAlertesDialog] = useState(false);
  const [alertesParcelle, setAlertesParcelle] = useState([]);
  const [parcelleForIntervention, setParcelleForIntervention] = useState(null);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [materielsListe, setMaterielsListe] = useState([]);
  const [materielSearch, setMaterielSearch] = useState('');
  const [showMaterielSuggestions, setShowMaterielSuggestions] = useState(false);
  // États pour la recherche EPHY
  const [produitSearch, setProduitSearch] = useState('');
  const [produitSuggestions, setProduitSuggestions] = useState([]);
  const [showProduitSuggestions, setShowProduitSuggestions] = useState(false);
  const [searchingProduit, setSearchingProduit] = useState(false);
  const [selectedProduitEphy, setSelectedProduitEphy] = useState(null);
  // États pour les cibles et suggestions IA de produits
  const [cibles, setCibles] = useState([]);
  const [suggestionsProduitsIA, setSuggestionsProduitsIA] = useState([]);
  const [loadingSuggestionsIA, setLoadingSuggestionsIA] = useState(false);
  // État pour la calculette produit (identique à InterventionsPage)
  const [showCalculette, setShowCalculette] = useState(null); // null ou {produit, volumeBouillie}
  
  const [newIntervention, setNewIntervention] = useState({
    type_intervention: 'travail_sol',
    operation: '',
    date_debut: new Date().toISOString().slice(0, 10),
    date_fin: '',
    heure_debut: '',
    heure_fin: '',
    operateur_id: '',
    operateur_nom: '',
    materiel_id: '',
    materiel_nom: '',
    surface_traitee: '',
    dose: '',
    unite_dose: 'L/ha',
    produit_nom: '',
    numero_amm: '',
    observations: '',
    statut: 'planifie',
    priorite: 'normale',
    duree_previsionnelle: '',
    // Notifications
    notifier_operateur: false,
    notifier_client: false,
    client_email: '',
    // Signature
    demander_signature: false,
    type_signature: 'email',
    // Météo
    meteo: '',
    temperature: '',
    vent_kmh: '',
    hygrometrie: '',
    // Paramètres pulvérisation
    volume_bouillie_ha: '',
    pression_bars: '',
    vitesse_avancement: '',
    type_buse: '',
    // Matériels (sélection multiple)
    materiels_selection: [],
    // Produits utilisés (EPHY/CASIA) - IDENTIQUE à InterventionsPage
    produits: []
  });
  
  // Charger les collaborateurs et matériels au montage
  useEffect(() => {
    const loadReferentiels = async () => {
      try {
        const [collabRes, materielRes] = await Promise.all([
          api.get('/team/collaborateurs').catch(() => ({ data: { collaborateurs: [] } })),
          api.get('/team/materiels').catch(() => ({ data: { materiels: [] } }))
        ]);
        setCollaborateurs(collabRes.data?.collaborateurs || []);
        setMaterielsListe(materielRes.data?.materiels || []);
        console.log('Matériels chargés dans parcellaire:', materielRes.data?.materiels?.length || 0);
      } catch (e) {
        console.log('Erreur chargement référentiels:', e);
      }
    };
    loadReferentiels();
  }, []);
  
  // Liste unique des cultures disponibles
  const availableCultures = [...new Set([
    ...cultures.map(c => c.nom),
    ...parcelles.map(p => p.culture_actuelle || p.culture_nom || p.culture).filter(Boolean)
  ])].sort();
  
  // Liste unique des exploitations depuis les parcelles
  const availableExploitations = [...new Set(
    parcelles.map(p => p.exploitation || p.exploitation_nom).filter(Boolean)
  )].sort();
  
  // Filtrer les parcelles selon la recherche et les filtres
  const filteredParcelles = parcelles.filter(p => {
    // Filtre recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchNom = p.nom?.toLowerCase().includes(query);
      const matchCulture = (p.culture_nom || p.culture_actuelle || p.culture || '')?.toLowerCase().includes(query);
      const matchIlot = p.ilot?.toString().includes(query);
      const matchExpl = (p.exploitation || p.exploitation_nom || '')?.toLowerCase().includes(query);
      const matchVariete = (p.variete || '')?.toLowerCase().includes(query);
      if (!matchNom && !matchCulture && !matchIlot && !matchExpl && !matchVariete) return false;
    }
    
    // Filtre cultures (sélection multiple)
    if (selectedCultures.length > 0) {
      const pCulture = p.culture_actuelle || p.culture_nom || p.culture || '';
      if (!selectedCultures.some(c => pCulture.toLowerCase().includes(c.toLowerCase()))) return false;
    }
    
    // Filtre exploitations (sélection multiple)
    if (selectedExploitations.length > 0) {
      const pExpl = p.exploitation || p.exploitation_nom || '';
      if (!selectedExploitations.includes(pExpl)) return false;
    }
    
    return true;
  });
  
  // Charger les exploitations
  useEffect(() => {
    const fetchExploitations = async () => {
      try {
        // Essayer d'abord /referentiels/exploitations puis fallback vers /exploitations
        let res;
        let exploitationsData = [];
        
        try {
          res = await api.get('/referentiels/exploitations');
          exploitationsData = res.data?.exploitations || res.data || [];
        } catch {
          // Ignorer l'erreur, on va essayer le fallback
        }
        
        // Si /referentiels/exploitations n'a rien retourné, essayer /exploitations
        if (!exploitationsData || exploitationsData.length === 0) {
          try {
            res = await api.get('/exploitations');
            exploitationsData = res.data?.exploitations || res.data || [];
          } catch {
            // Ignorer l'erreur
          }
        }
        
        setExploitations(exploitationsData);
      } catch (e) {
        console.error('Erreur chargement exploitations:', e);
        // Fallback: utiliser les exploitations des parcelles
        setExploitations([]);
      }
    };
    fetchExploitations();
  }, []);
  
  // Fonction pour supprimer une parcelle
  const deleteParcelle = async (parcelleId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette parcelle ?')) return;
    
    try {
      await api.delete(`/parcellaire/parcelles/${parcelleId}`);
      toast.success('Parcelle supprimée');
      setSelectedParcelle(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Fonction pour supprimer un réseau
  const deleteReseau = async (reseauId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce réseau ?')) return;
    
    try {
      await api.delete(`/parcellaire/reseaux/${reseauId}`);
      toast.success('Réseau supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Géolocalisation temps réel
  useEffect(() => {
    if (!trackingEnabled) return;
    
    let watchId = null;
    
    if ("geolocation" in navigator) {
      watchId = navigator.geolocation.watchPosition(
        async (position) => {
          const pos = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            altitude: position.coords.altitude,
            accuracy: position.coords.accuracy,
            speed: position.coords.speed,
            heading: position.coords.heading
          };
          setMyPosition(pos);
          
          // Envoyer au serveur
          try {
            await api.post('/geo/position', pos);
          } catch (e) {
            console.error('Erreur envoi position:', e);
          }
        },
        (error) => {
          console.error('Erreur géolocalisation:', error);
          toast.error('Impossible d\'accéder à la géolocalisation');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 5000
        }
      );
    }
    
    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, [trackingEnabled]);
  
  // Charger les positions de l'équipe
  useEffect(() => {
    if (!showTeamTracking) return;
    
    const fetchTeamPositions = async () => {
      try {
        const res = await api.get('/geo/team');
        setTeamPositions(res.data.team_positions || []);
      } catch (e) {
        console.error('Erreur récup positions équipe:', e);
      }
    };
    
    const fetchTrackerPositions = async () => {
      try {
        const res = await api.get('/cuma/trackers/positions');
        setTrackerPositions(res.data.positions || []);
      } catch (e) {
        // Pas critique si le module CUMA n'est pas utilisé
      }
    };
    
    const fetchCarteLive = async () => {
      try {
        const res = await api.get('/geoloc/carte-live');
        setCarteLive({ chauffeurs: res.data.chauffeurs || [], interventions: res.data.interventions || [] });
      } catch (e) {
        // couche optionnelle
      }
    };

    fetchTeamPositions();
    fetchTrackerPositions();
    fetchCarteLive();
    const liveInterval = setInterval(fetchCarteLive, 30000);
    const interval = setInterval(fetchTeamPositions, 10000);
    const trackerInterval = setInterval(fetchTrackerPositions, 30000); // Trackers: refresh 30s
    
    return () => { clearInterval(interval); clearInterval(trackerInterval); clearInterval(liveInterval); };
  }, [showTeamTracking]);
  
  const fetchData = async () => {
    try {
      const [parcellesRes, reseauxRes, culturesRes, batimentsRes] = await Promise.all([
        api.get(`/parcellaire/parcelles?campagne=${campagne}`),
        api.get('/parcellaire/reseaux'),
        api.get('/parcellaire/cultures'),
        api.get('/parcellaire/batiments').catch(() => ({ data: { batiments: [] } }))
      ]);
      
      // Support both old format (array) and new format ({parcelles, stats})
      const parcellesData = Array.isArray(parcellesRes.data) 
        ? parcellesRes.data 
        : parcellesRes.data.parcelles || [];
      
      setParcelles(parcellesData);
      
      // Build stats if not provided
      if (parcellesRes.data.stats) {
        setStats(parcellesRes.data.stats);
      } else {
        // Calculate stats from parcelles
        const statsCalc = { total_surface: 0, nb_parcelles: parcellesData.length, par_culture: {} };
        parcellesData.forEach(p => {
          const surface = p.surface_ha || p.surface || 0;
          statsCalc.total_surface += surface;
          const culture = p.culture_actuelle || p.culture_nom || 'Non affecté';
          if (!statsCalc.par_culture[culture]) {
            statsCalc.par_culture[culture] = { surface: 0, count: 0, nom: culture, couleur: p.culture_couleur || '#9ca3af' };
          }
          statsCalc.par_culture[culture].surface += surface;
          statsCalc.par_culture[culture].count += 1;
        });
        statsCalc.total_surface = Math.round(statsCalc.total_surface * 100) / 100;
        setStats(statsCalc);
      }
      
      const reseauxData = reseauxRes.data.reseaux || [];
      setReseaux(reseauxData);
      setCultures(culturesRes.data.cultures || []);
      setBatiments(batimentsRes.data?.batiments || []);
      
      // Centrer sur la première parcelle avec coordonnées
      const firstWithCoords = parcellesData.find(p => p.center || p.coordonnees);
      if (firstWithCoords) {
        const coords = firstWithCoords.center || firstWithCoords.coordonnees;
        setMapCenter([coords.lat, coords.lng]);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [campagne]);
  
  const toggleReseauVisibility = (type) => {
    setVisibleReseaux(prev => ({...prev, [type]: !prev[type]}));
  };
  
  const handleDraw = async (points, mode, surfaceCalculee) => {
    if (mode === 'parcelle') {
      // Préparer la géométrie et ouvrir le formulaire
      const geometry = {
        type: 'Polygon',
        coordinates: [[...points.map(p => [p[1], p[0]]), [points[0][1], points[0][0]]]]
      };
      
      const center = {
        lat: points.reduce((s, p) => s + p[0], 0) / points.length,
        lng: points.reduce((s, p) => s + p[1], 0) / points.length
      };
      
      setPendingGeometry(geometry);
      setPendingCenter(center);
      setPendingSurface(parseFloat(surfaceCalculee) || 0);
      setNewParcelle(prev => ({
        ...prev,
        nom: `Parcelle ${parcelles.length + 1}`,
        exploitation_id: exploitations[0]?.id || ''
      }));
      setShowCreateDialog(true);
      
    } else if (mode === 'reseau') {
      // Préparer la géométrie du réseau
      const geometry = {
        type: 'LineString',
        coordinates: points.map(p => [p[1], p[0]])
      };
      
      // Calculer la longueur du réseau
      let longueur = 0;
      for (let i = 0; i < points.length - 1; i++) {
        const R = 6371000;
        const dLat = (points[i+1][0] - points[i][0]) * Math.PI / 180;
        const dLng = (points[i+1][1] - points[i][1]) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(points[i][0] * Math.PI / 180) * Math.cos(points[i+1][0] * Math.PI / 180) *
                  Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        longueur += R * c;
      }
      
      setPendingGeometry(geometry);
      setPendingLongueur(Math.round(longueur));
      setNewReseau(prev => ({
        ...prev,
        nom: `Réseau ${reseaux.length + 1}`
      }));
      setShowCreateReseauDialog(true);
    }
  };
  
  // Fonction Me localiser
  const handleLocateMe = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Géolocalisation non disponible sur votre appareil");
      return;
    }
    
    setLocatingMe(true);
    toast.info("Recherche de votre position...", { duration: 2000 });
    
    // Essayer d'abord avec haute précision (GPS)
    const tryHighAccuracy = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setMyPosition(pos);
          setMapCenter([pos.latitude, pos.longitude]);
          setMapZoom(17);
          setLocatingMe(false);
          toast.success(`Position trouvée ! (±${Math.round(pos.accuracy)}m)`);
          
          // Activer le tracking
          if (!trackingEnabled) {
            setTrackingEnabled(true);
          }
        },
        (error) => {
          console.warn('GPS haute précision échoué, essai basse précision:', error);
          // Fallback: essayer sans haute précision (réseau/wifi)
          tryLowAccuracy();
        },
        { enableHighAccuracy: true, timeout: 25000, maximumAge: 15000 }
      );
    };
    
    const tryLowAccuracy = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setMyPosition(pos);
          setMapCenter([pos.latitude, pos.longitude]);
          setMapZoom(15); // Zoom moins fort car moins précis
          setLocatingMe(false);
          toast.success(`Position approximative (±${Math.round(pos.accuracy)}m)`);
          
          if (!trackingEnabled) {
            setTrackingEnabled(true);
          }
        },
        async (error) => {
          console.error('Erreur géolocalisation:', error);
          // Fallback ultime : géolocalisation par IP (ipapi.co)
          try {
            const r = await fetch('https://ipapi.co/json/');
            if (r.ok) {
              const d = await r.json();
              if (d.latitude && d.longitude) {
                setMyPosition({ latitude: d.latitude, longitude: d.longitude, accuracy: 5000 });
                setMapCenter([d.latitude, d.longitude]);
                setMapZoom(13);
                setLocatingMe(false);
                toast.warning(`Position approximative par IP (ville ${d.city}, précision faible)`, { duration: 5000 });
                return;
              }
            }
          } catch { /* fallback IP échoue silencieusement */ }
          setLocatingMe(false);
          if (error.code === 1) {
            toast.error("Accès refusé. Autorisez la localisation dans les paramètres.", { duration: 6000 });
          } else if (error.code === 2) {
            toast.error("Position indisponible. Vérifie que le GPS est activé et que tu es dehors.", { duration: 6000 });
          } else if (error.code === 3) {
            toast.error("Délai dépassé. Réessaye à l'extérieur pour un meilleur signal GPS.", { duration: 6000 });
          } else {
            toast.error("Impossible de trouver ta position. Réessaye.");
          }
        },
        { enableHighAccuracy: false, timeout: 30000, maximumAge: 300000 }
      );
    };
    
    tryHighAccuracy();
  };
  
  // Démarrer la création depuis ma position
  const startDrawFromPosition = (mode) => {
    if (mode === 'parcelle') {
      // Fermer le dialog et démarrer le dessin
      setShowCreateFromPosition(false);
      setDrawMode('parcelle');
      toast.info("Dessinez le contour de la parcelle sur la carte", { duration: 5000 });
    } else if (mode === 'client') {
      setCreateFromPositionMode('client');
    }
  };
  
  // Créer un client/exploitation depuis ma position
  const createClientFromPosition = async () => {
    if (!newClientFromPosition.nom || !myPosition) {
      toast.error("Nom du client requis");
      return;
    }
    
    try {
      const response = await api.post('/referentiels/exploitations', {
        nom: newClientFromPosition.nom,
        contact: newClientFromPosition.contact,
        telephone: newClientFromPosition.telephone,
        latitude: myPosition.latitude,
        longitude: myPosition.longitude,
        adresse: `Position GPS: ${myPosition.latitude.toFixed(6)}, ${myPosition.longitude.toFixed(6)}`
      });
      
      toast.success(`Client "${newClientFromPosition.nom}" créé avec succès !`);
      setShowCreateFromPosition(false);
      setNewClientFromPosition({ nom: '', contact: '', telephone: '' });
      setCreateFromPositionMode(null);
      
      // Rafraîchir la liste des exploitations
      fetchData();
    } catch (error) {
      console.error('Erreur création client:', error);
      toast.error("Erreur lors de la création du client");
    }
  };
  
  // Créer la parcelle avec le formulaire complet
  const createParcelle = async () => {
    try {
      // Récupérer les infos de la culture
      let culture_nom = null, culture_couleur = null;
      if (newParcelle.culture_id) {
        const culture = cultures.find(c => c.id === newParcelle.culture_id);
        if (culture) {
          culture_nom = culture.nom;
          culture_couleur = culture.couleur;
        }
      }
      
      await api.post('/parcellaire/parcelles', {
        nom: newParcelle.nom,
        surface_ha: pendingSurface,
        geometry: pendingGeometry,
        center: pendingCenter,
        campagne,
        exploitation_id: newParcelle.exploitation_id,
        exploitation: newParcelle.exploitation,
        culture_id: newParcelle.culture_id,
        culture_nom,
        culture_couleur,
        ilot_pac: newParcelle.ilot_pac,
        commune: newParcelle.commune,
        code_cadastral: newParcelle.code_cadastral,
        variete: newParcelle.variete,
        mode_culture: newParcelle.mode_culture,
        irrigation: newParcelle.irrigation,
        notes: newParcelle.notes
      });
      
      toast.success(`Parcelle "${newParcelle.nom}" créée : ${pendingSurface} ha`);
      setShowCreateDialog(false);
      setPendingGeometry(null);
      setNewParcelle({
        nom: '', exploitation_id: '', exploitation: '', culture_id: '', ilot_pac: '',
        commune: '', code_cadastral: '', variete: '', mode_culture: 'conventionnel',
        irrigation: false, notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erreur création:', error);
      toast.error('Erreur lors de la création de la parcelle');
    }
  };
  
  // Créer le réseau avec le formulaire
  const createReseau = async () => {
    try {
      await api.post('/parcellaire/reseaux', {
        nom: newReseau.nom,
        type_reseau: newReseau.type_reseau,
        statut: newReseau.statut,
        geometry: pendingGeometry,
        longueur_m: pendingLongueur,
        diametre_mm: newReseau.diametre_mm ? parseInt(newReseau.diametre_mm) : null,
        materiau: newReseau.materiau,
        notes: newReseau.notes
      });
      
      toast.success(`Réseau "${newReseau.nom}" créé : ${pendingLongueur} m`);
      setShowCreateReseauDialog(false);
      setPendingGeometry(null);
      setNewReseau({
        nom: '', type_reseau: 'irrigation', statut: 'prevu',
        diametre_mm: '', materiau: 'pehd', notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erreur création:', error);
      toast.error('Erreur lors de la création du réseau');
    }
  };
  
  // Créer une intervention sur une parcelle
  const createIntervention = async () => {
    if (!parcelleForIntervention) return;
    
    try {
      await api.post('/parcellaire/interventions', {
        parcelle_id: parcelleForIntervention.id,
        type_intervention: newIntervention.type_intervention,
        operation: newIntervention.operation || null,
        date_debut: newIntervention.date_debut,
        date_fin: newIntervention.date_fin || null,
        heure_debut: newIntervention.heure_debut || null,
        heure_fin: newIntervention.heure_fin || null,
        operateur_id: newIntervention.operateur_id || null,
        operateur_nom: newIntervention.operateur_nom || null,
        materiel_id: newIntervention.materiel_id || null,
        materiel_nom: newIntervention.materiel_nom || null,
        surface_traitee: newIntervention.surface_traitee ? parseFloat(newIntervention.surface_traitee) : (parcelleForIntervention.surface_ha || parcelleForIntervention.surface),
        dose: newIntervention.dose ? parseFloat(newIntervention.dose) : null,
        unite_dose: newIntervention.unite_dose,
        produit_nom: newIntervention.produit_nom || null,
        observations: newIntervention.observations || null,
        statut: newIntervention.statut,
        priorite: newIntervention.priorite,
        duree_previsionnelle: newIntervention.duree_previsionnelle || null,
        // Notifications
        notifier_operateur: newIntervention.notifier_operateur,
        notifier_client: newIntervention.notifier_client,
        client_email: newIntervention.client_email || null,
        // Signature
        demander_signature: newIntervention.demander_signature,
        type_signature: newIntervention.type_signature,
        // Météo
        meteo: newIntervention.meteo || null,
        temperature: newIntervention.temperature || null,
        vent_kmh: newIntervention.vent_kmh || null,
        hygrometrie: newIntervention.hygrometrie || null,
        // Paramètres pulvérisation
        volume_bouillie_ha: newIntervention.volume_bouillie_ha || null,
        pression_bars: newIntervention.pression_bars || null,
        vitesse_avancement: newIntervention.vitesse_avancement || null,
        type_buse: newIntervention.type_buse || null,
        // Matériels (sélection multiple)
        materiels: (newIntervention.materiels_selection || []).filter(m => m.id).map(m => ({
          id: m.id,
          nom: m.nom,
          type: m.type
        })),
        // Produits utilisés (EPHY/CASIA) - IDENTIQUE à InterventionsPage
        produits: (newIntervention.produits || []).map(p => ({
          type: p.type,
          nom: p.nom,
          numero_amm: p.numero_amm,
          firme: p.firme,
          fournisseur: p.fournisseur,
          quantite: p.quantite ? parseFloat(p.quantite) : null,
          unite: p.unite,
          cout_unitaire: p.cout_unitaire ? parseFloat(p.cout_unitaire) : null,
          cout_total: p.cout_total ? parseFloat(p.cout_total) : null,
          dose_max: p.dose_max,
          contient_cuivre: p.contient_cuivre,
          substances_actives: p.substances_actives,
          cultures_autorisees: p.cultures_autorisees
        }))
      });
      
      toast.success(`Intervention créée sur "${parcelleForIntervention.nom}"`);
      setShowInterventionDialog(false);
      setParcelleForIntervention(null);
      resetInterventionForm();
      // Refresh selected parcelle details if still open
      if (selectedParcelle && selectedParcelle.id === parcelleForIntervention.id) {
        setSelectedParcelle({...selectedParcelle}); // Force re-render
      }
    } catch (error) {
      console.error('Erreur création intervention:', error);
      toast.error('Erreur lors de la création de l\'intervention');
    }
  };
  
  // Réinitialiser le formulaire d'intervention
  const resetInterventionForm = () => {
    setNewIntervention({
      type_intervention: 'travail_sol',
      operation: '',
      date_debut: new Date().toISOString().slice(0, 10),
      date_fin: '',
      heure_debut: '',
      heure_fin: '',
      operateur_id: '',
      operateur_nom: '',
      materiel_id: '',
      materiel_nom: '',
      surface_traitee: '',
      dose: '',
      unite_dose: 'L/ha',
      produit_nom: '',
      numero_amm: '',
      observations: '',
      statut: 'planifie',
      priorite: 'normale',
      duree_previsionnelle: '',
      notifier_operateur: false,
      notifier_client: false,
      client_email: '',
      demander_signature: false,
      type_signature: 'email',
      meteo: '',
      temperature: '',
      vent_kmh: '',
      hygrometrie: '',
      volume_bouillie_ha: '',
      pression_bars: '',
      vitesse_avancement: '',
      type_buse: '',
      materiels_selection: [],
      produits: []
    });
    setMaterielSearch('');
  };
  
  // Ouvrir le dialog de création d'intervention avec vérification alertes
  const handleCreateIntervention = async (parcelle) => {
    setParcelleForIntervention(parcelle);
    setNewIntervention(prev => ({
      ...prev,
      surface_traitee: parcelle.surface_ha || parcelle.surface || ''
    }));
    // Réinitialiser les états de recherche
    setCibles([]);
    setSuggestionsProduitsIA([]);
    setProduitSearch('');
    setProduitSuggestions([]);
    setSelectedProduitEphy(null);
    
    // Vérifier les alertes sur la parcelle
    try {
      const res = await api.post('/alertes-parcelle/verifier', {
        parcelle_id: parcelle.id,
        type_intervention: '',
        materiel_id: null
      });
      
      if (res.data.nb_dangers > 0) {
        // Afficher les alertes d'abord
        setAlertesParcelle(res.data.alertes || []);
        setShowAlertesDialog(true);
      } else if (res.data.nb_alertes > 0) {
        // Points d'attention - afficher un toast mais continuer
        toast.warning(`${res.data.nb_alertes} point(s) d'attention sur cette parcelle`, { duration: 4000 });
        setShowInterventionDialog(true);
      } else {
        // Pas d'alerte, ouvrir directement
        setShowInterventionDialog(true);
      }
    } catch (err) {
      console.error('Erreur vérification alertes:', err);
      // En cas d'erreur, ouvrir quand même le dialog
      setShowInterventionDialog(true);
    }
  };
  
  // Continuer après alertes
  const handleContinueAfterAlertes = () => {
    setShowAlertesDialog(false);
    setShowInterventionDialog(true);
  };
  
  // Copier les parcelles d'une campagne à l'autre
  const copyCampagne = async (fromYear) => {
    try {
      const res = await api.get(`/parcellaire/parcelles?campagne=${fromYear}`);
      const oldParcelles = res.data.parcelles || [];
      
      if (oldParcelles.length === 0) {
        toast.error(`Aucune parcelle trouvée en ${fromYear}`);
        return;
      }
      
      let created = 0;
      for (const p of oldParcelles) {
        try {
          await api.post('/parcellaire/parcelles', {
            nom: p.nom,
            surface_ha: p.surface_ha,
            geometry: p.geometry,
            center: p.center,
            campagne: campagne, // Nouvelle campagne
            exploitation_id: p.exploitation_id,
            culture_id: p.culture_id,
            culture_nom: p.culture_nom,
            culture_couleur: p.culture_couleur,
            ilot_pac: p.ilot_pac,
            commune: p.commune,
            code_cadastral: p.code_cadastral,
            mode_culture: p.mode_culture,
            irrigation: p.irrigation,
            notes: `Copié depuis campagne ${fromYear}`
          });
          created++;
        } catch (e) {
          console.error('Erreur copie parcelle:', e);
        }
      }
      
      toast.success(`${created} parcelles copiées de ${fromYear} vers ${campagne}`);
      fetchData();
    } catch (error) {
      console.error('Erreur copie campagne:', error);
      toast.error('Erreur lors de la copie');
    }
  };
  
  // Recherche de ville via API backend (proxy Nominatim)
  const searchCity = async (query, setResults) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    
    setSearchingCity(true);
    try {
      const response = await api.get(`/parcellaire/search/city?q=${encodeURIComponent(query)}&limit=5`);
      const results = response.data.results || [];
      setResults(results);
    } catch (error) {
      console.error('Erreur recherche ville:', error);
      setResults([]);
    } finally {
      setSearchingCity(false);
    }
  };
  
  // Debounce pour la recherche de ville
  useEffect(() => {
    const timer = setTimeout(() => {
      if (citySearchA) searchCity(citySearchA, setCitySuggestionsA);
    }, 300);
    return () => clearTimeout(timer);
  }, [citySearchA]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (citySearchB) searchCity(citySearchB, setCitySuggestionsB);
    }, 300);
    return () => clearTimeout(timer);
  }, [citySearchB]);
  
  // Sélectionner un point depuis une ville, parcelle ou exploitation
  const selectPointFromSource = (point, coords, label) => {
    if (point === 'A') {
      setItineraireData(prev => ({ ...prev, pointA: coords, pointALabel: label }));
      setCitySearchA('');
      setCitySuggestionsA([]);
      toast.success(`Point A défini : ${label}`);
    } else {
      setItineraireData(prev => ({ ...prev, pointB: coords, pointBLabel: label }));
      setCitySearchB('');
      setCitySuggestionsB([]);
      toast.success(`Point B défini : ${label}`);
    }
  };
  
  const getReseauStyle = (reseau) => {
    const colors = {
      irrigation: '#3b82f6',
      electricite: '#eab308',
      drainage: '#06b6d4',
      cloture: '#78716c'
    };
    
    const dashArrays = {
      en_place: null,
      prevu: '10, 5',
      a_faire: '2, 5'
    };
    
    return {
      color: colors[reseau.type_reseau] || '#666',
      weight: 3,
      dashArray: dashArrays[reseau.statut]
    };
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-200px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // Composant FAB (Floating Action Button) - Visible sur tous les écrans
  const MobileFloatingActions = () => (
    <div className="fixed bottom-24 right-4 z-[999] flex flex-col gap-3">
      {/* Bouton principal - Menu actions rapides */}
      {!drawMode && (
        <>
          {/* Bouton Itinéraire - Utilise automatiquement ma position comme départ */}
          <Button
            size="lg"
            className="w-14 h-14 rounded-full shadow-xl bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => {
              setShowItineraire(true);
              // Utiliser automatiquement ma position comme point de départ
              if (myPosition) {
                setItineraireData(prev => ({
                  ...prev,
                  pointA: { lat: myPosition.latitude, lng: myPosition.longitude },
                  pointALabel: '📍 Ma position'
                }));
              } else {
                // Essayer de récupérer la position si pas encore disponible
                if ("geolocation" in navigator) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const pos = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy
                      };
                      setMyPosition(pos);
                      setItineraireData(prev => ({
                        ...prev,
                        pointA: { lat: pos.latitude, lng: pos.longitude },
                        pointALabel: '📍 Ma position'
                      }));
                      toast.success(`Position GPS utilisée comme départ (±${Math.round(pos.accuracy)}m)`);
                    },
                    (error) => {
                      console.log('GPS non disponible, sélection manuelle requise');
                    },
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                  );
                }
              }
            }}
            data-testid="fab-itineraire"
          >
            <Route className="w-6 h-6" />
          </Button>
          
          {/* Bouton Assistant Vocal Itinéraire */}
          <Button
            size="lg"
            className={cn(
              "w-14 h-14 rounded-full shadow-xl text-white",
              showVoiceCommand ? "bg-red-500 hover:bg-red-600" : "bg-gradient-to-br from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
            )}
            onClick={() => setShowVoiceCommand(!showVoiceCommand)}
            data-testid="fab-voice-assistant"
          >
            {showVoiceCommand ? <X className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </Button>
          
          {/* Bouton Intervention rapide */}
          <Button
            size="lg"
            className="w-14 h-14 rounded-full shadow-xl bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => {
              setShowInterventionDialog(true);
            }}
            data-testid="fab-intervention"
          >
            <Tractor className="w-6 h-6" />
          </Button>
          
          {/* Bouton Me localiser */}
          <Button
            size="lg"
            className={cn(
              "w-14 h-14 rounded-full shadow-xl text-white",
              myPosition ? "bg-orange-500 hover:bg-orange-600" : "bg-orange-400 hover:bg-orange-500"
            )}
            onClick={handleLocateMe}
            disabled={locatingMe}
            data-testid="fab-locate"
          >
            {locatingMe ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <Navigation className="w-6 h-6" />
            )}
          </Button>
          
          {/* Bouton Créer parcelle */}
          <Button
            size="lg"
            className="w-14 h-14 rounded-full shadow-xl bg-green-600 hover:bg-green-700 text-white"
            onClick={() => { 
              setDrawMode('parcelle'); 
              toast.info('Touchez la carte pour placer les points de la parcelle', { duration: 4000 });
            }}
            data-testid="fab-add-parcelle"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </>
      )}
      
      {/* === ASSISTANT VOCAL ITINÉRAIRE === */}
      {showVoiceCommand && (
        <div className="fixed inset-x-4 bottom-24 z-[2000] md:left-auto md:right-4 md:w-96">
          <Card className="bg-background/95 backdrop-blur shadow-2xl border-2 border-primary/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Assistant Itinéraire
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowVoiceCommand(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Zone de saisie vocale/texte */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="Ex: Emmène-moi à la parcelle des Oliviers..."
                    value={voiceCommand}
                    onChange={(e) => setVoiceCommand(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVoiceCommand(voiceCommand)}
                    className={cn(
                      "pr-10",
                      isListening && "border-red-500 animate-pulse"
                    )}
                    data-testid="voice-command-input"
                  />
                  {processingVoice && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                <Button
                  size="icon"
                  variant={isListening ? "destructive" : "outline"}
                  onClick={toggleVoiceListening}
                  className={cn(isListening && "animate-pulse")}
                  data-testid="voice-mic-button"
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Indicateur d'écoute */}
              {isListening && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span>Je vous écoute...</span>
                </div>
              )}
              
              {/* Réponse de l'IA */}
              {voiceResponse && (
                <div className={cn(
                  "p-3 rounded-lg text-sm",
                  voiceResponse.success ? "bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200" : "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
                )}>
                  <p>{voiceResponse.message_ia}</p>
                  {voiceResponse.destination_nom && (
                    <p className="mt-1 font-medium">🎯 {voiceResponse.destination_nom}</p>
                  )}
                  {voiceResponse.vehicule_agricole && (
                    <Badge variant="secondary" className="mt-2">
                      <Tractor className="w-3 h-3 mr-1" /> Mode tracteur activé
                    </Badge>
                  )}
                </div>
              )}
              
              {/* Exemples de commandes */}
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium">Exemples :</p>
                <div className="flex flex-wrap gap-1">
                  {[
                    "Parcelle des Oliviers",
                    "Champ du Moulin avec tracteur",
                    "Aller au siège"
                  ].map((ex) => (
                    <Button
                      key={ex}
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => {
                        setVoiceCommand(ex);
                        handleVoiceCommand(ex);
                      }}
                    >
                      {ex}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Bouton Lancer */}
              <Button
                className="w-full"
                onClick={() => handleVoiceCommand(voiceCommand)}
                disabled={!voiceCommand.trim() || processingVoice}
              >
                {processingVoice ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Route className="w-4 h-4 mr-2" />
                    Lancer l'itinéraire
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-4" data-testid="parcellaire-page">
      {/* ===== VERSION MOBILE - Header simplifié ===== */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold">Parcellaire</h1>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              {filteredParcelles.length} parcelles
            </Badge>
            <Badge variant="secondary" className="text-sm">
              {filteredParcelles.reduce((acc, p) => acc + (p.surface_ha || p.surface || 0), 0).toFixed(0)} ha
            </Badge>
          </div>
        </div>
        
        {/* Recherche compacte mobile */}
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-8 h-10 text-sm"
            data-testid="search-parcelle-mobile"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setSearchQuery('')}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* ===== VERSION DESKTOP - Header complet ===== */}
      <div className="hidden md:flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Parcellaire</h1>
          <p className="text-muted-foreground">Assolement, réseaux et calculs IA</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {/* Boutons principaux d'ajout */}
          <Button 
            onClick={() => { setDrawMode('parcelle'); toast.info('Cliquez sur la carte pour dessiner la parcelle'); }}
            className="bg-green-600 hover:bg-green-700"
            data-testid="btn-add-parcelle"
          >
            <Plus className="w-4 h-4 mr-2" />
            Parcelle
          </Button>
          
          <Button 
            onClick={() => { setDrawMode('reseau'); toast.info('Cliquez sur la carte pour tracer le réseau'); }}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="btn-add-reseau"
          >
            <Plus className="w-4 h-4 mr-2" />
            Réseau
          </Button>

          <Button 
            onClick={() => { setPlacingBatiment(true); toast.info('Cliquez sur la carte pour placer le bâtiment'); }}
            variant="outline"
            className="border-amber-500 text-amber-700 hover:bg-amber-50"
            data-testid="btn-add-batiment"
          >
            <Plus className="w-4 h-4 mr-2" />
            Bâtiment
          </Button>
          
          <div className="border-l mx-2" />
          
          <Select value={campagne.toString()} onValueChange={v => setCampagne(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={() => setShowCalculator(true)}>
            <Calculator className="w-4 h-4 mr-2" />
            Calculs
          </Button>
          
          <Button variant="outline" onClick={() => {
            setShowItineraire(true);
            // Utiliser automatiquement ma position comme point de départ
            if (myPosition) {
              setItineraireData(prev => ({
                ...prev,
                pointA: { lat: myPosition.latitude, lng: myPosition.longitude },
                pointALabel: '📍 Ma position'
              }));
            }
          }}>
            <Route className="w-4 h-4 mr-2" />
            Itinéraire
          </Button>
          
          <Button variant="outline">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          
          {/* Copier depuis campagne précédente */}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" title="Reprendre parcelles d'une autre campagne">
                <RefreshCw className="w-4 h-4 mr-2" />
                Copier campagne
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Copier les parcelles d'une campagne</DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Cette action va copier toutes les parcelles d'une campagne précédente vers la campagne {campagne}.
                </p>
                <div className="flex gap-2">
                  {[campagne - 1, campagne - 2].map(year => (
                    <Button
                      key={year}
                      variant="outline"
                      className="flex-1"
                      onClick={() => copyCampagne(year)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Copier {year}
                    </Button>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Bouton Me localiser */}
          <Button 
            variant="outline"
            className={cn(
              "border-orange-500 text-orange-600 hover:bg-orange-50",
              myPosition && "bg-orange-100"
            )}
            onClick={handleLocateMe}
            data-testid="btn-locate-me"
          >
            <MapPin className={cn("w-4 h-4 mr-2", locatingMe && "animate-pulse")} />
            {locatingMe ? 'Localisation...' : 'Me localiser'}
          </Button>
          
          {/* Créer depuis ma position */}
          {myPosition && (
            <Dialog open={showCreateFromPosition} onOpenChange={setShowCreateFromPosition}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="btn-create-from-position"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer ici
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Créer depuis ma position</DialogTitle>
                </DialogHeader>
                <div className="py-4 space-y-4">
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm font-medium text-orange-800">Position actuelle</p>
                    <p className="text-xs text-orange-600">
                      {myPosition.latitude.toFixed(6)}, {myPosition.longitude.toFixed(6)}
                      {myPosition.accuracy && ` (±${Math.round(myPosition.accuracy)}m)`}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Que souhaitez-vous créer ?</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="outline" 
                        className="h-20 flex-col"
                        onClick={() => startDrawFromPosition('parcelle')}
                      >
                        <Map className="w-6 h-6 mb-1 text-green-600" />
                        <span>Parcelle</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="h-20 flex-col"
                        onClick={() => startDrawFromPosition('client')}
                      >
                        <Users className="w-6 h-6 mb-1 text-blue-600" />
                        <span>Client/Exploitation</span>
                      </Button>
                    </div>
                  </div>
                  
                  {createFromPositionMode === 'client' && (
                    <div className="space-y-3 border-t pt-3">
                      <div>
                        <Label htmlFor="new-client-nom">Nom du client/exploitation *</Label>
                        <Input 
                          id="new-client-nom"
                          value={newClientFromPosition.nom}
                          onChange={(e) => setNewClientFromPosition({...newClientFromPosition, nom: e.target.value})}
                          placeholder="Ex: GAEC Les Oliviers"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-client-contact">Nom du contact</Label>
                        <Input 
                          id="new-client-contact"
                          value={newClientFromPosition.contact}
                          onChange={(e) => setNewClientFromPosition({...newClientFromPosition, contact: e.target.value})}
                          placeholder="Ex: Jean Dupont"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-client-tel">Téléphone</Label>
                        <Input 
                          id="new-client-tel"
                          value={newClientFromPosition.telephone}
                          onChange={(e) => setNewClientFromPosition({...newClientFromPosition, telephone: e.target.value})}
                          placeholder="06 XX XX XX XX"
                        />
                      </div>
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={createClientFromPosition}
                        disabled={!newClientFromPosition.nom}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Créer le client
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      
      {/* Barre de recherche et filtres - DESKTOP UNIQUEMENT */}
      <div className="hidden md:flex flex-col gap-4 p-4 bg-muted/50 rounded-lg">
        {/* Recherche principale - Plus grande */}
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Rechercher une parcelle, exploitation, culture, variété..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-10 h-12 text-lg bg-background border-2 focus:border-primary"
            data-testid="search-parcelle"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
              onClick={() => setSearchQuery('')}
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>
        
        {/* Filtres avec cases à cocher */}
        <div className="flex gap-3 flex-wrap items-center">
          {/* Filtre Exploitations */}
          <Popover open={showExploitationFilter} onOpenChange={setShowExploitationFilter}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "min-w-[280px] h-11 justify-between text-left font-normal",
                  selectedExploitations.length > 0 && "border-primary bg-primary/5"
                )}
                data-testid="filter-exploitations"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  <span>
                    {selectedExploitations.length === 0 
                      ? "Toutes les exploitations" 
                      : selectedExploitations.length === 1
                        ? selectedExploitations[0]
                        : `${selectedExploitations.length} exploitations`
                    }
                  </span>
                </div>
                <ChevronDown className={cn("w-4 h-4 transition-transform", showExploitationFilter && "rotate-180")} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <div className="p-3 border-b">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">Exploitations</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      if (selectedExploitations.length === availableExploitations.length) {
                        setSelectedExploitations([]);
                      } else {
                        setSelectedExploitations([...availableExploitations]);
                      }
                    }}
                  >
                    {selectedExploitations.length === availableExploitations.length ? (
                      <>
                        <X className="w-3 h-3 mr-1" />
                        Tout désélectionner
                      </>
                    ) : (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Tout sélectionner
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <ScrollArea className="h-[300px]">
                <div className="p-2 space-y-1">
                  {availableExploitations.map((expl) => (
                    <label
                      key={expl}
                      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={selectedExploitations.includes(expl)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedExploitations(prev => [...prev, expl]);
                          } else {
                            setSelectedExploitations(prev => prev.filter(e => e !== expl));
                          }
                        }}
                      />
                      <span className="text-sm flex-1 truncate">{expl}</span>
                      <Badge variant="secondary" className="text-xs">
                        {parcelles.filter(p => (p.exploitation || p.exploitation_nom) === expl).length}
                      </Badge>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          
          {/* Filtre Cultures */}
          <Popover open={showCultureFilter} onOpenChange={setShowCultureFilter}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "min-w-[220px] h-11 justify-between text-left font-normal",
                  selectedCultures.length > 0 && "border-primary bg-primary/5"
                )}
                data-testid="filter-cultures"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  <span>
                    {selectedCultures.length === 0 
                      ? "Toutes les cultures" 
                      : selectedCultures.length === 1
                        ? selectedCultures[0]
                        : `${selectedCultures.length} cultures`
                    }
                  </span>
                </div>
                <ChevronDown className={cn("w-4 h-4 transition-transform", showCultureFilter && "rotate-180")} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
              <div className="p-3 border-b">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">Cultures</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      if (selectedCultures.length === availableCultures.length) {
                        setSelectedCultures([]);
                      } else {
                        setSelectedCultures([...availableCultures]);
                      }
                    }}
                  >
                    {selectedCultures.length === availableCultures.length ? (
                      <>
                        <X className="w-3 h-3 mr-1" />
                        Tout désélectionner
                      </>
                    ) : (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Tout sélectionner
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <ScrollArea className="h-[300px]">
                <div className="p-2 space-y-1">
                  {availableCultures.map((culture) => {
                    // Utiliser le nouveau système de couleurs et icônes
                    const style = getCultureStyle(culture);
                    
                    return (
                      <label
                        key={culture}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedCultures.includes(culture)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedCultures(prev => [...prev, culture]);
                            } else {
                              setSelectedCultures(prev => prev.filter(c => c !== culture));
                            }
                          }}
                        />
                        <div 
                          className="w-6 h-6 rounded flex-shrink-0 flex items-center justify-center text-sm" 
                          style={{ backgroundColor: style.color }}
                        >
                          {style.icon}
                        </div>
                        <span className="text-sm flex-1 truncate">{culture}</span>
                        <Badge variant="secondary" className="text-xs">
                          {parcelles.filter(p => (p.culture_actuelle || p.culture_nom || p.culture) === culture).length}
                        </Badge>
                      </label>
                    );
                  })}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          
          {/* Badges des filtres actifs + bouton réinitialiser */}
          {(selectedExploitations.length > 0 || selectedCultures.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Filtres actifs:</span>
              {selectedExploitations.map(expl => (
                <Badge 
                  key={expl} 
                  variant="secondary" 
                  className="cursor-pointer hover:bg-destructive/20"
                  onClick={() => setSelectedExploitations(prev => prev.filter(e => e !== expl))}
                >
                  {expl}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              {selectedCultures.map(culture => (
                <Badge 
                  key={culture} 
                  variant="outline" 
                  className="cursor-pointer hover:bg-destructive/20"
                  onClick={() => setSelectedCultures(prev => prev.filter(c => c !== culture))}
                >
                  {culture}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              <Button 
                variant="ghost" 
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-destructive"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCultures([]);
                  setSelectedExploitations([]);
                }}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Tout effacer
              </Button>
            </div>
          )}
          
          {/* Compteur de résultats */}
          <div className="ml-auto flex items-center gap-2">
            <Badge variant="outline" className="text-sm py-1 px-3">
              {filteredParcelles.length} / {parcelles.length} parcelles
            </Badge>
            <Badge variant="outline" className="text-sm py-1 px-3">
              {filteredParcelles.reduce((acc, p) => acc + (p.surface_ha || p.surface || 0), 0).toFixed(1)} ha
            </Badge>
          </div>
        </div>
        
        {/* Résultats de recherche (suggestions) */}
        {searchQuery && filteredParcelles.length > 0 && filteredParcelles.length <= 20 && (
          <div className="bg-background border rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {filteredParcelles.slice(0, 10).map(p => (
              <button
                key={p.id}
                className="w-full text-left px-4 py-3 hover:bg-muted flex items-center justify-between border-b last:border-b-0"
                onClick={() => {
                  setSelectedParcelle(p);
                  if (p.center) {
                    setMapCenter([p.center.lat, p.center.lng]);
                  }
                  setSearchQuery('');
                }}
              >
                <div>
                  <p className="font-medium">{p.nom}</p>
                  <p className="text-sm text-muted-foreground">
                    {p.exploitation || p.exploitation_nom} • {p.culture_nom || p.culture || 'Sans culture'} • {p.surface_ha || p.surface} ha
                  </p>
                </div>
                <MapPin className="w-5 h-5 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Carte - Plein écran sur mobile, hauteur fixe sur desktop */}
      <div className="relative h-[calc(100vh-140px)] md:h-[calc(100vh-350px)] rounded-xl overflow-hidden border">
        
        {/* Indicateur mode dessin sur mobile */}
        {drawMode && (
          <div className="md:hidden absolute top-2 left-2 right-2 z-[1001] bg-green-600 text-white p-3 rounded-lg shadow-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              <span className="font-medium">
                {drawMode === 'parcelle' ? 'Dessiner la parcelle' : 'Tracer le réseau'}
              </span>
            </div>
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={() => setDrawMode(null)}
              className="h-8"
            >
              Annuler
            </Button>
          </div>
        )}
        
        {/* Sélecteur de couches - Simplifié sur mobile */}
        <div className="absolute left-2 md:left-4 bottom-2 md:bottom-4 z-[1000] bg-background/95 backdrop-blur rounded-lg shadow-lg p-1 flex gap-1">
          <Button
            size="sm"
            variant={mapLayer === 'satellite' ? 'default' : 'ghost'}
            onClick={() => setMapLayer('satellite')}
            className="h-8 px-2"
          >
            <Satellite className="w-4 h-4 mr-1" />
            Satellite
          </Button>
          <Button
            size="sm"
            variant={mapLayer === 'cadastre' ? 'default' : 'ghost'}
            onClick={() => setMapLayer('cadastre')}
            className="h-8 px-2"
          >
            <Layers className="w-4 h-4 mr-1" />
            Cadastre
          </Button>
          <Button
            size="sm"
            variant={mapLayer === 'osm' ? 'default' : 'ghost'}
            onClick={() => setMapLayer('osm')}
            className="h-8 px-2"
          >
            <MapIcon className="w-4 h-4 mr-1" />
            Plan
          </Button>
          
          <div className="border-l mx-1" />
          
          {/* Bouton tracking équipe */}
          <Button
            size="sm"
            variant={showTeamTracking ? 'default' : 'ghost'}
            onClick={() => setShowTeamTracking(!showTeamTracking)}
            className="h-8 px-2"
            title="Afficher l'équipe"
          >
            <Users className="w-4 h-4 mr-1" />
            Équipe & interventions
            {(teamPositions.length + carteLive.chauffeurs.length + carteLive.interventions.length) > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5" data-testid="carte-live-count">
                {teamPositions.length + carteLive.chauffeurs.length + carteLive.interventions.length}
              </Badge>
            )}
          </Button>
          
          {/* Bouton activer mon tracking */}
          <Button
            size="sm"
            variant={trackingEnabled ? 'default' : 'outline'}
            onClick={() => {
              setTrackingEnabled(!trackingEnabled);
              if (!trackingEnabled) {
                toast.success('📍 Tracking GPS activé');
              } else {
                toast.info('Tracking GPS désactivé');
                api.delete('/geo/position').catch(() => {});
              }
            }}
            className={cn("h-8 px-2", trackingEnabled && "bg-green-600 hover:bg-green-700")}
            title="Activer ma géolocalisation"
          >
            <Navigation className={cn("w-4 h-4", trackingEnabled && "animate-pulse")} />
          </Button>
          
          {/* Bouton Travaux ETA */}
          <Button
            size="sm"
            variant={showTracesTravaux ? 'default' : 'ghost'}
            onClick={() => setShowTracesTravaux(!showTracesTravaux)}
            className={cn("h-8 px-2", showTracesTravaux && "bg-orange-600 hover:bg-orange-700")}
            title="Travaux communaux & reseaux"
          >
            <Route className="w-4 h-4 mr-1" />
            Travaux
          </Button>
        </div>
        
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          className="h-full w-full"
          scrollWheelZoom={true}
          touchZoom={true}
          dragging={true}
          doubleClickZoom={true}
          tap={true}
          zoomControl={true}
        >
          {/* Couche de base : Satellite (ESRI World Imagery) */}
          {mapLayer === 'satellite' && (
            <>
              <TileLayer
                attribution='&copy; ESRI'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              {/* Couche labels (noms des villes, routes) par-dessus le satellite */}
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                opacity={1}
              />
            </>
          )}
          
          {/* Couche cadastre + satellite dessous */}
          {mapLayer === 'cadastre' && (
            <>
              <TileLayer
                attribution='&copy; ESRI'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              <WMSTileLayer
                url="https://data.geopf.fr/wms-r"
                layers="CADASTRALPARCELS.PARCELLAIRE_EXPRESS"
                format="image/png"
                transparent={true}
                opacity={0.8}
                version="1.3.0"
                attribution='&copy; IGN - Parcellaire Express'
              />
            </>
          )}
          
          {/* Couche OSM classique */}
          {mapLayer === 'osm' && (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}
          
          {/* Parcelles */}
          {filteredParcelles.map(parcelle => {
            // Support both old and new coordinate format
            let positions = [];
            
            if (parcelle.geometry && parcelle.geometry.type === 'Polygon') {
              positions = parcelle.geometry.coordinates[0].map(c => [c[1], c[0]]);
            } else if (parcelle.coordonnees) {
              // Old format: just a point - create a small square around it
              const lat = parcelle.coordonnees.lat;
              const lng = parcelle.coordonnees.lng;
              const offset = 0.002; // ~200m
              positions = [
                [lat - offset, lng - offset],
                [lat - offset, lng + offset],
                [lat + offset, lng + offset],
                [lat + offset, lng - offset],
              ];
            }
            
            if (positions.length === 0) return null;
            
            // Utiliser le style basé sur le nom de la culture
            const cultureName = parcelle.culture_nom || parcelle.culture_actuelle || parcelle.culture || '';
            const cultureStyle = getCultureStyle(cultureName);
            const color = cultureStyle.color;
            
            // Surbrillance si parcelle sélectionnée
            const isSelected = selectedParcelle?.id === parcelle.id;
            
            return (
              <Polygon
                key={parcelle.id}
                positions={positions}
                pathOptions={{
                  color: isSelected ? '#ffffff' : color,
                  fillColor: color,
                  fillOpacity: isSelected ? 0.7 : 0.5,
                  weight: isSelected ? 4 : 2
                }}
                eventHandlers={{
                  click: () => setSelectedParcelle(parcelle)
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-base">{cultureStyle.icon}</span>
                      <p className="font-bold">{parcelle.nom}</p>
                    </div>
                    <p>{parcelle.surface_ha || parcelle.surface} ha</p>
                    <p className="text-muted-foreground">{cultureName || 'Non affecté'}</p>
                  </div>
                </Popup>
              </Polygon>
            );
          })}
          
          {/* Réseaux */}
          {reseaux.map(reseau => {
            if (!reseau.geometry || !visibleReseaux[reseau.type_reseau]) return null;
            
            if (reseau.geometry.type === 'LineString') {
              const positions = reseau.geometry.coordinates.map(c => [c[1], c[0]]);
              return (
                <Polyline
                  key={reseau.id}
                  positions={positions}
                  pathOptions={getReseauStyle(reseau)}
                >
                  <Popup className="reseau-popup">
                    <div className="text-sm space-y-2 min-w-[200px]">
                      <p className="font-bold text-base border-b pb-1">{reseau.nom || 'Réseau sans nom'}</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <span className="text-muted-foreground">Type:</span>
                        <span className="font-medium">{reseau.type_reseau}</span>
                        <span className="text-muted-foreground">Statut:</span>
                        <Badge variant="outline" className="text-xs h-5">{reseau.statut}</Badge>
                        {reseau.longueur_m && (
                          <>
                            <span className="text-muted-foreground">Longueur:</span>
                            <span className="font-medium">{reseau.longueur_m}m</span>
                          </>
                        )}
                        {reseau.diametre_mm && (
                          <>
                            <span className="text-muted-foreground">Diamètre:</span>
                            <span className="font-medium">{reseau.diametre_mm}mm</span>
                          </>
                        )}
                        {reseau.source_alimentation && (
                          <>
                            <span className="text-muted-foreground">Source:</span>
                            <span className="font-medium">{reseau.source_alimentation}</span>
                          </>
                        )}
                        {reseau.type_distribution && (
                          <>
                            <span className="text-muted-foreground">Distribution:</span>
                            <span className="font-medium">{reseau.type_distribution}</span>
                          </>
                        )}
                      </div>
                      {reseau.notes && (
                        <p className="text-xs text-muted-foreground italic border-t pt-1">{reseau.notes}</p>
                      )}
                      <div className="flex gap-2 pt-2 border-t">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 h-8"
                          onClick={() => {
                            // TODO: Ouvrir dialog modification
                            toast.info('Modification à venir');
                          }}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Modifier
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          className="flex-1 h-8"
                          onClick={() => deleteReseau(reseau.id)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                        </Button>
                      </div>
                    </div>
                  </Popup>
                </Polyline>
              );
            }
            return null;
          })}
          
          {/* Mode dessin + sélection itinéraire */}
          <DrawingMode 
            onDraw={handleDraw} 
            mode={drawMode} 
            onModeChange={setDrawMode}
            onCancel={() => setDrawMode(null)}
            selectingPoint={selectingPoint}
            onPointSelected={(point, coords) => {
              if (point === 'A') {
                setItineraireData(prev => ({ ...prev, pointA: coords, pointALabel: '📍 Carte' }));
                toast.success('Point de départ défini ✅');
              } else if (point === 'B') {
                setItineraireData(prev => ({ ...prev, pointB: coords, pointBLabel: '📍 Carte' }));
                toast.success('Point d\'arrivée défini ✅');
              }
              setSelectingPoint(null);
            }}
          />

          {/* Mode placement bâtiment */}
          {placingBatiment && <BatimentPlacementHandler onPlace={(lat, lng) => {
            setNewBatiment(prev => ({ ...prev, latitude: lat, longitude: lng }));
            setShowCreateBatiment(true);
            setPlacingBatiment(false);
          }} />}
          
          {/* Carte tout-en-un : chauffeurs GPS + interventions du jour */}
          {showTeamTracking && carteLive.chauffeurs.filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lon)).map((c) => (
            <CircleMarker key={`gps-${c.user_id}`} center={[c.lat, c.lon]} radius={9}
              pathOptions={{ color: '#fff', weight: 2, fillColor: c.is_active ? '#16a34a' : '#9ca3af', fillOpacity: 0.95 }}>
              <Popup>
                <div data-testid="carte-chauffeur-popup" className="text-sm">
                  <b>{c.user_name || 'Chauffeur'}</b><br />
                  {c.is_active ? 'En activité' : 'Hors ligne'} · vu le {(c.last_seen_at || '').slice(0, 16).replace('T', ' ')}
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {showTeamTracking && carteLive.interventions.map((i) => (
            <CircleMarker key={`int-${i.id}`} center={[i.lat, i.lng]} radius={11}
              pathOptions={{ color: '#9a3412', weight: 2, fillColor: '#f97316', fillOpacity: 0.85, dashArray: '3' }}>
              <Popup>
                <div data-testid="carte-intervention-popup" className="text-sm">
                  <b>{i.operation || i.type_intervention || 'Intervention'}</b><br />
                  {i.parcelle_nom}{i.client_nom ? ` — ${i.client_nom}` : ''}<br />
                  Statut : {i.statut || 'prévue'}{i.operateur ? ` · ${i.operateur}` : ''}
                </div>
              </Popup>
            </CircleMarker>
          ))}

          {/* Positions de l'équipe - Style Snapchat avec avatars et bulles */}
          {showTeamTracking && teamPositions.map((member) => (
            <Marker
              key={member.user_id}
              position={[member.latitude, member.longitude]}
              icon={L.divIcon({
                className: 'team-marker-snapchat',
                html: `
                  <div class="relative group cursor-pointer" style="transform: translate(-24px, -24px);">
                    <!-- Avatar principal avec bordure de statut -->
                    <div class="relative">
                      ${member.avatar_url ? `
                        <img 
                          src="${member.avatar_url}" 
                          alt="${member.user_name}"
                          class="w-12 h-12 rounded-full object-cover border-3 shadow-lg ${
                            member.status === 'active' ? 'border-green-500 animate-pulse' : 
                            member.status === 'idle' ? 'border-yellow-500' : 'border-gray-400'
                          }"
                          onerror="this.onerror=null; this.src=''; this.style.display='none'; this.nextElementSibling.style.display='flex';"
                        />
                        <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 border-3 shadow-lg items-center justify-center text-white font-bold text-lg hidden ${
                          member.status === 'active' ? 'border-green-500' : 
                          member.status === 'idle' ? 'border-yellow-500' : 'border-gray-400'
                        }">
                          ${member.user_name?.charAt(0) || '?'}
                        </div>
                      ` : `
                        <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 border-3 shadow-lg flex items-center justify-center text-white font-bold text-lg ${
                          member.status === 'active' ? 'border-green-500 animate-pulse' : 
                          member.status === 'idle' ? 'border-yellow-500' : 'border-gray-400'
                        }">
                          ${member.user_name?.charAt(0) || '?'}
                        </div>
                      `}
                      
                      <!-- Indicateur de statut (point) -->
                      <div class="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white shadow ${
                        member.status === 'active' ? 'bg-green-500' : 
                        member.status === 'idle' ? 'bg-yellow-500' : 'bg-gray-400'
                      }"></div>
                      
                      <!-- Emoji status personnalisé (si défini) -->
                      ${member.emoji_status && member.emoji_status !== '🚜' ? `
                        <div class="absolute -top-1 -left-1 w-5 h-5 bg-white rounded-full shadow flex items-center justify-center text-xs">
                          ${member.emoji_status}
                        </div>
                      ` : ''}
                      
                      <!-- Badge vitesse si en mouvement -->
                      ${member.speed && member.speed > 0.5 ? `
                        <div class="absolute -top-1 -right-1 px-1.5 py-0.5 bg-orange-500 rounded-full text-white text-[10px] font-bold shadow">
                          ${Math.round(member.speed * 3.6)} km/h
                        </div>
                      ` : ''}
                    </div>
                    
                    <!-- Bulle d'info au survol - Style Snapchat -->
                    <div class="absolute left-1/2 -translate-x-1/2 top-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      <div class="bg-black/90 text-white px-3 py-2 rounded-xl shadow-lg whitespace-nowrap">
                        <p class="font-bold text-sm">${member.user_name}</p>
                        ${member.current_activity ? `<p class="text-xs text-gray-300">${member.current_activity}</p>` : ''}
                        <div class="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-black/90 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                `,
                iconSize: [48, 48],
                iconAnchor: [0, 0]
              })}
            >
              <Popup className="snapchat-popup">
                <div className="text-sm min-w-[180px]">
                  <div className="flex items-center gap-3 mb-2">
                    {member.avatar_url ? (
                      <img 
                        src={member.avatar_url} 
                        alt={member.user_name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-blue-500"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                        {member.user_name?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="font-bold">{member.user_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {member.status === 'active' ? '🟢 Actif' : member.status === 'idle' ? '🟡 Arrêté' : '⚫ Hors ligne'}
                      </p>
                    </div>
                  </div>
                  
                  {member.current_activity && (
                    <div className="bg-muted/50 rounded-lg px-2 py-1.5 mb-2">
                      <p className="text-xs font-medium">📍 {member.current_activity}</p>
                    </div>
                  )}
                  
                  {member.speed && member.speed > 0.5 && (
                    <p className="text-sm">🚜 {Math.round(member.speed * 3.6)} km/h</p>
                  )}
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    Màj: {new Date(member.updated_at).toLocaleTimeString('fr-FR')}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
          
          {/* Positions des traceurs GPS machines (CUMA/transporteurs/animaux) */}
          {trackerPositions && trackerPositions.map((tracker) => (
            <Marker
              key={tracker.machine_id}
              position={[tracker.latitude, tracker.longitude]}
              icon={L.divIcon({
                className: 'tracker-marker',
                html: `
                  <div style="transform: translate(-16px, -16px);">
                    <div style="width: 32px; height: 32px; background: linear-gradient(135deg, #f59e0b, #d97706); border: 2px solid white; border-radius: 8px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-size: 16px;">
                      ${tracker.type_machine === 'tracteur' ? '<span style="font-size:14px;">T</span>' : 
                        tracker.type_machine === 'broyeur' ? '<span style="font-size:14px;">B</span>' : 
                        tracker.type_machine === 'animal' ? '<span style="font-size:14px;">A</span>' : 
                        '<span style="font-size:14px; color:white;">M</span>'}
                    </div>
                    <div style="text-align:center; margin-top: 2px;">
                      <span style="font-size: 9px; background: rgba(0,0,0,0.7); color: white; padding: 1px 4px; border-radius: 4px; white-space: nowrap;">
                        ${tracker.machine_nom || 'Machine'}
                      </span>
                    </div>
                  </div>
                `,
                iconSize: [32, 48],
                iconAnchor: [0, 0]
              })}
            >
              <Popup>
                <div className="text-sm min-w-[160px]">
                  <p className="font-bold text-amber-600">{tracker.machine_nom || 'Machine'}</p>
                  <p className="text-xs text-muted-foreground">{tracker.type_machine}</p>
                  {tracker.speed > 0 && <p className="text-xs mt-1">Vitesse: {Math.round(tracker.speed)} km/h</p>}
                  {tracker.battery && <p className="text-xs">Batterie: {tracker.battery}%</p>}
                  <p className="text-xs text-muted-foreground mt-1">
                    Màj: {new Date(tracker.timestamp).toLocaleTimeString('fr-FR')}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
          
          {/* Ma position avec animation pulsante */}
          {myPosition && (
            <>
              {/* Cercle pulsant externe (animation) */}
              <CircleMarker
                center={[myPosition.latitude, myPosition.longitude]}
                radius={20}
                pathOptions={{
                  color: '#3b82f6',
                  fillColor: '#3b82f6',
                  fillOpacity: 0.2,
                  weight: 2,
                  className: 'pulse-marker-outer'
                }}
              />
              {/* Point central bleu */}
              <CircleMarker
                center={[myPosition.latitude, myPosition.longitude]}
                radius={8}
                pathOptions={{
                  color: '#ffffff',
                  fillColor: '#3b82f6',
                  fillOpacity: 1,
                  weight: 3
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-bold">📍 Ma position</p>
                    {myPosition.accuracy && (
                      <p className="text-xs">Précision: ±{Math.round(myPosition.accuracy)}m</p>
                    )}
                    {myPosition.speed && myPosition.speed > 0.5 && (
                      <p>Vitesse: {Math.round(myPosition.speed * 3.6)} km/h</p>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            </>
          )}
          
          {/* Cercle de précision autour de ma position */}
          {myPosition && myPosition.accuracy && (
            <CircleMarker
              center={[myPosition.latitude, myPosition.longitude]}
              radius={myPosition.accuracy / 10}
              pathOptions={{
                color: '#3b82f6',
                fillColor: '#3b82f6',
                fillOpacity: 0.1,
                weight: 1
              }}
            />
          )}
          
          {/* Marqueurs itinéraire - Point A (départ) */}
          {itineraireData.pointA && (
            <Marker
              position={[itineraireData.pointA.lat, itineraireData.pointA.lng]}
              icon={L.divIcon({
                className: 'itineraire-marker-a',
                html: `<div class="w-8 h-8 rounded-full bg-green-500 border-3 border-white shadow-lg flex items-center justify-center text-white font-bold text-sm">A</div>`,
                iconSize: [32, 32],
                iconAnchor: [16, 16]
              })}
            >
              <Popup>
                <div className="text-sm font-medium">📍 Point de départ</div>
                {itineraireData.pointALabel && <div className="text-xs text-gray-600">{itineraireData.pointALabel}</div>}
              </Popup>
            </Marker>
          )}
          
          {/* Marqueurs itinéraire - Point B (arrivée) */}
          {itineraireData.pointB && (
            <Marker
              position={[itineraireData.pointB.lat, itineraireData.pointB.lng]}
              icon={L.divIcon({
                className: 'itineraire-marker-b',
                html: `<div class="w-8 h-8 rounded-full bg-red-500 border-3 border-white shadow-lg flex items-center justify-center text-white font-bold text-sm">B</div>`,
                iconSize: [32, 32],
                iconAnchor: [16, 16]
              })}
            >
              <Popup>
                <div className="text-sm font-medium">🏁 Point d'arrivée</div>
                {itineraireData.pointBLabel && <div className="text-xs text-gray-600">{itineraireData.pointBLabel}</div>}
              </Popup>
            </Marker>
          )}
          
          {/* Itinéraire calculé (route réelle poids lourds) */}
          {calculatedRoute && calculatedRoute.length > 0 && (
            <Polyline
              positions={calculatedRoute}
              pathOptions={{
                color: '#2563eb',
                weight: 5,
                opacity: 0.8
              }}
            />
          )}
          
          {/* Ligne directe en pointillés (avant calcul ou en attente) */}
          {itineraireData.pointA && itineraireData.pointB && !calculatedRoute && (
            <Polyline
              positions={[
                [itineraireData.pointA.lat, itineraireData.pointA.lng],
                [itineraireData.pointB.lat, itineraireData.pointB.lng]
              ]}
              pathOptions={{
                color: '#f97316',
                weight: 3,
                dashArray: '10, 10'
              }}
            />
          )}

          {/* ===== BATIMENTS / INFRASTRUCTURES ===== */}
          {showBatiments && batiments.map(bat => {
            const BATIMENT_ICONS = {
              hangar: { emoji: '🏗️', color: '#8B4513' },
              atelier: { emoji: '🔧', color: '#6366f1' },
              bureaux: { emoji: '🏢', color: '#2563eb' },
              distillerie: { emoji: '🔥', color: '#dc2626' },
              cave: { emoji: '🍷', color: '#7c3aed' },
              serre: { emoji: '🌿', color: '#16a34a' },
              silo: { emoji: '🏭', color: '#ca8a04' },
              entrepot: { emoji: '📦', color: '#78716c' },
              garage: { emoji: '🚜', color: '#475569' },
              chai: { emoji: '🍇', color: '#9333ea' },
              autre: { emoji: '📍', color: '#64748b' },
            };
            const style = BATIMENT_ICONS[bat.type_batiment] || BATIMENT_ICONS.autre;
            return (
              <Marker
                key={bat.id}
                position={[bat.latitude, bat.longitude]}
                icon={L.divIcon({
                  className: 'batiment-marker',
                  html: `<div style="background:${style.color};width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);font-size:18px;cursor:pointer;">${style.emoji}</div>`,
                  iconSize: [36, 36],
                  iconAnchor: [18, 18]
                })}
              >
                <Popup>
                  <div className="text-sm min-w-[200px]">
                    <div className="font-bold text-base mb-1">{style.emoji} {bat.nom}</div>
                    <div className="text-gray-600 capitalize mb-1">{bat.type_batiment}</div>
                    {bat.description && <div className="text-gray-500 text-xs mb-1">{bat.description}</div>}
                    {bat.nom_entreprise && <div className="text-xs"><strong>Entreprise :</strong> {bat.nom_entreprise}</div>}
                    {bat.surface_m2 && <div className="text-xs"><strong>Surface :</strong> {bat.surface_m2} m²</div>}
                    <div className="text-xs mt-1">
                      {bat.accessible_a_tous ? '🌐 Accessible à tous' : '🔒 Accès restreint'}
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button 
                        onClick={async () => {
                          if (window.confirm('Supprimer ce bâtiment ?')) {
                            try {
                              await api.delete(`/parcellaire/batiments/${bat.id}`);
                              fetchData();
                            } catch(e) { console.error(e); }
                          }
                        }}
                        className="text-xs text-red-600 hover:underline"
                      >Supprimer</button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          
          {/* Traces Travaux ETA / Reseaux */}
          <TracesTravaux
            token={localStorage.getItem('token')}
            societeId={localStorage.getItem('activeSocieteId')}
            visible={showTracesTravaux}
            onToggle={() => setShowTracesTravaux(!showTracesTravaux)}
          />
        </MapContainer>
        
        {/* Légendes */}
        <CultureLegend 
          stats={stats} 
          cultures={cultures}
          onFilterClick={(id) => console.log('Filter:', id)}
        />
        
        <ReseauLegend 
          visible={visibleReseaux}
          onToggle={toggleReseauVisibility}
          collapsed={reseauLegendCollapsed}
          setCollapsed={setReseauLegendCollapsed}
        />
        
        {/* Panneau équipe en temps réel - Style Snapchat */}
        {showTeamTracking && teamPositions.length > 0 && (
          <div className="absolute left-4 bottom-20 z-[1000] bg-background/95 backdrop-blur rounded-2xl shadow-lg p-3 max-w-xs border">
            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span>Équipe en direct</span>
              <Badge variant="secondary" className="ml-auto">{teamPositions.length}</Badge>
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {teamPositions.map(member => (
                <div 
                  key={member.user_id}
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted cursor-pointer transition-colors"
                  onClick={() => setMapCenter([member.latitude, member.longitude])}
                >
                  {/* Avatar avec statut */}
                  <div className="relative flex-shrink-0">
                    {member.avatar_url ? (
                      <img 
                        src={member.avatar_url} 
                        alt={member.user_name}
                        className={cn(
                          "w-10 h-10 rounded-full object-cover border-2",
                          member.status === 'active' ? "border-green-500" : 
                          member.status === 'idle' ? "border-yellow-500" : "border-gray-400"
                        )}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className={cn(
                        "w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold border-2",
                        member.status === 'active' ? "border-green-500" : 
                        member.status === 'idle' ? "border-yellow-500" : "border-gray-400"
                      )}>
                        {member.user_name?.charAt(0)}
                      </div>
                    )}
                    {/* Point de statut */}
                    <div className={cn(
                      "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background",
                      member.status === 'active' ? "bg-green-500" : 
                      member.status === 'idle' ? "bg-yellow-500" : "bg-gray-400"
                    )} />
                  </div>
                  
                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{member.user_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {member.current_activity || (
                        member.speed && member.speed > 0.5 
                          ? `🚜 ${Math.round(member.speed * 3.6)} km/h` 
                          : member.status === 'active' ? 'Arrêté' : 'Hors ligne'
                      )}
                    </p>
                  </div>
                  
                  {/* Indicateur vitesse */}
                  {member.speed && member.speed > 0.5 && (
                    <Badge className="bg-orange-500 text-white text-[10px]">
                      {Math.round(member.speed * 3.6)}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Détails parcelle */}
        <ParcelleDetails
          parcelle={selectedParcelle}
          onClose={() => setSelectedParcelle(null)}
          onEdit={(p) => console.log('Edit:', p)}
          onDelete={deleteParcelle}
          onCreateIntervention={handleCreateIntervention}
        />
      </div>
      
      {/* Calculateur */}
      <DebitCalculator 
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
      />
      
      {/* Dialog création parcelle */}
      {/* Dialog quick-create Exploitation (depuis fin parcelle) */}
      <Dialog open={showQuickCreateExploitation} onOpenChange={setShowQuickCreateExploitation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Nouvelle exploitation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Nom de l'exploitation *</Label>
              <Input
                value={quickExploitation.nom}
                onChange={(e) => setQuickExploitation(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Ex: EARL Frères Fabre"
                data-testid="qc-expl-nom"
              />
            </div>
            <div className="space-y-1">
              <Label>Commune *</Label>
              <Input
                value={quickExploitation.commune}
                onChange={(e) => setQuickExploitation(prev => ({ ...prev, commune: e.target.value }))}
                placeholder="Ex: Mirabel-aux-Baronnies"
                data-testid="qc-expl-commune"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={!quickExploitation.nom || searchingPappers}
              onClick={async () => {
                setSearchingPappers(true);
                setPappersResults([]);
                try {
                  const q = quickExploitation.commune
                    ? `${quickExploitation.nom} ${quickExploitation.commune}`
                    : quickExploitation.nom;
                  const r = await api.get(`/pappers/search?q=${encodeURIComponent(q)}`);
                  setPappersResults(r.data?.results || []);
                  if ((r.data?.results || []).length === 0) {
                    toast.info('Aucun résultat API Gouv — tu peux créer manuellement');
                  }
                } catch { toast.error('Recherche API Gouv indisponible'); }
                finally { setSearchingPappers(false); }
              }}
              data-testid="btn-search-pappers"
            >
              {searchingPappers ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Search className="w-3 h-3 mr-2" />}
              Rechercher sur l'API Gouv (SIRET auto)
            </Button>
            {pappersResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1 border rounded p-2 bg-muted/30">
                {pappersResults.slice(0, 5).map((e, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setQuickExploitation(prev => ({
                        ...prev,
                        nom: e.nom || e.denomination || prev.nom,
                        siret: e.siret || e.siege?.siret || '',
                        adresse: e.siege?.adresse_ligne_1 || e.adresse || '',
                        commune: e.siege?.ville || e.commune || prev.commune,
                      }));
                      setPappersResults([]);
                      toast.success('Pré-rempli depuis API Gouv');
                    }}
                    className="w-full text-left text-xs p-2 rounded hover:bg-background"
                    data-testid={`pappers-result-${idx}`}
                  >
                    <div className="font-semibold">{e.nom || e.denomination}</div>
                    <div className="text-muted-foreground">{e.siren || e.siret} · {e.siege?.ville || e.commune || ''}</div>
                  </button>
                ))}
              </div>
            )}
            {quickExploitation.siret && (
              <div className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                ✓ SIRET : {quickExploitation.siret} · {quickExploitation.adresse}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickCreateExploitation(false)}>Annuler</Button>
            <Button
              onClick={async () => {
                if (!quickExploitation.nom || !quickExploitation.commune) {
                  toast.error('Nom et commune obligatoires');
                  return;
                }
                try {
                  const r = await api.post('/referentiels/exploitations', {
                    nom: quickExploitation.nom,
                    commune: quickExploitation.commune,
                    siret: quickExploitation.siret || null,
                    adresse: quickExploitation.adresse || null,
                  });
                  const newExpl = r.data?.exploitation || r.data;
                  // Ajouter à la liste et sélectionner
                  setExploitations(prev => [...prev, newExpl]);
                  setNewParcelle(prev => ({ ...prev, exploitation: newExpl.nom, exploitation_id: newExpl.id }));
                  setQuickExploitation({ nom: '', commune: '', siret: '', adresse: '' });
                  setPappersResults([]);
                  setShowQuickCreateExploitation(false);
                  toast.success(`Exploitation « ${newExpl.nom} » créée`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Erreur création');
                }
              }}
              data-testid="btn-save-quick-exploitation"
            >
              <Plus className="w-4 h-4 mr-2" />Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-600" />
              Nouvelle Parcelle - {pendingSurface} ha
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4 py-4">
              {/* Surface calculée */}
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Surface calculée :</span>
                <span className="text-2xl font-bold text-green-600">{pendingSurface} ha</span>
              </div>
              
              {/* Infos principales */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de la parcelle *</Label>
                  <Input
                    value={newParcelle.nom}
                    onChange={(e) => setNewParcelle(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder="Ex: Côte Sud, Les Vignes..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Exploitation</Label>
                  <div className="flex gap-2">
                    <Select
                      value={newParcelle.exploitation || "none"}
                      onValueChange={(v) => setNewParcelle(prev => ({ ...prev, exploitation: v === "none" ? "" : v }))}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Choisir exploitation..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {exploitations.map(e => (
                          <SelectItem key={e.id} value={e.nom}>{e.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => setShowQuickCreateExploitation(true)}
                      title="Nouvelle exploitation"
                      data-testid="btn-new-exploitation-quick"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Culture</Label>
                  <Select
                    value={newParcelle.culture_id || "none"}
                    onValueChange={(v) => setNewParcelle(prev => ({ ...prev, culture_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir culture..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {cultures.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Variété</Label>
                  <Input
                    value={newParcelle.variete}
                    onChange={(e) => setNewParcelle(prev => ({ ...prev, variete: e.target.value }))}
                    placeholder="Ex: Cabernet Sauvignon..."
                  />
                </div>
              </div>
              
              {/* Infos PAC */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Informations PAC / Cadastre</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Îlot PAC</Label>
                    <Input
                      value={newParcelle.ilot_pac}
                      onChange={(e) => setNewParcelle(prev => ({ ...prev, ilot_pac: e.target.value }))}
                      placeholder="Ex: 15"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Commune</Label>
                    <Input
                      value={newParcelle.commune}
                      onChange={(e) => setNewParcelle(prev => ({ ...prev, commune: e.target.value }))}
                      placeholder="Ex: Saint-Émilion"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Réf. cadastrale</Label>
                    <Input
                      value={newParcelle.code_cadastral}
                      onChange={(e) => setNewParcelle(prev => ({ ...prev, code_cadastral: e.target.value }))}
                      placeholder="Ex: AB 123"
                    />
                  </div>
                </div>
              </div>
              
              {/* Mode de culture */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  Mode de culture
                  {(newParcelle.mode_culture === 'bio' || newParcelle.mode_culture === 'conversion') && (
                    <Badge className="bg-green-600 text-white">🌿 Agriculture Biologique</Badge>
                  )}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'conventionnel', label: 'Conventionnel', bio: false },
                    { id: 'bio', label: '🌿 Bio', bio: true },
                    { id: 'conversion', label: '🔄 Conversion Bio', bio: true },
                    { id: 'raisonne', label: 'Raisonné', bio: false },
                  ].map(mode => (
                    <Button
                      key={mode.id}
                      type="button"
                      variant={newParcelle.mode_culture === mode.id ? "default" : "outline"}
                      size="sm"
                      className={newParcelle.mode_culture === mode.id && mode.bio ? "bg-green-600 hover:bg-green-700" : ""}
                      onClick={() => setNewParcelle(prev => ({ ...prev, mode_culture: mode.id, is_bio: mode.bio }))}
                    >
                      {mode.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={newParcelle.notes}
                  onChange={(e) => setNewParcelle(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Remarques, historique..."
                />
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setPendingGeometry(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={createParcelle}
              disabled={!newParcelle.nom}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer la parcelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog création réseau */}
      <Dialog open={showCreateReseauDialog} onOpenChange={setShowCreateReseauDialog}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ruler className="w-5 h-5 text-blue-600" />
              Nouveau Réseau
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Longueur calculée */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Longueur calculée :</span>
              <span className="text-2xl font-bold text-blue-600">{pendingLongueur} m</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du réseau *</Label>
                <Input
                  value={newReseau.nom}
                  onChange={(e) => setNewReseau(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder="Ex: Irrigation Zone A"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Type de réseau</Label>
                <Select
                  value={newReseau.type_reseau}
                  onValueChange={(v) => setNewReseau(prev => ({ ...prev, type_reseau: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="irrigation">💧 Irrigation</SelectItem>
                    <SelectItem value="electricite">⚡ Électricité</SelectItem>
                    <SelectItem value="drainage">🌊 Drainage</SelectItem>
                    <SelectItem value="cloture">🚧 Clôture</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Section Irrigation spécifique */}
            {newReseau.type_reseau === 'irrigation' && (
              <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg space-y-3">
                <h4 className="font-medium text-cyan-800 dark:text-cyan-200 flex items-center gap-2">
                  <Droplets className="w-4 h-4" /> Configuration Irrigation
                </h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Source d'alimentation</Label>
                    <Select
                      value={newReseau.source_alimentation}
                      onValueChange={(v) => setNewReseau(prev => ({ ...prev, source_alimentation: v }))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="forage">🔵 Forage</SelectItem>
                        <SelectItem value="prise_eau">🚰 Prise d'eau</SelectItem>
                        <SelectItem value="bassin">🏊 Bassin / Réserve</SelectItem>
                        <SelectItem value="cuve">🛢️ Cuve</SelectItem>
                        <SelectItem value="riviere">🌊 Rivière / Canal</SelectItem>
                        <SelectItem value="reseau_public">💧 Réseau public</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Type de distribution</Label>
                    <Select
                      value={newReseau.type_distribution}
                      onValueChange={(v) => setNewReseau(prev => ({ ...prev, type_distribution: v }))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="goutte_a_goutte">💧 Goutte à goutte</SelectItem>
                        <SelectItem value="asperseur">🌧️ Asperseur</SelectItem>
                        <SelectItem value="micro_aspersion">💦 Micro-aspersion</SelectItem>
                        <SelectItem value="rampe">📏 Rampe</SelectItem>
                        <SelectItem value="pivot">🔄 Pivot</SelectItem>
                        <SelectItem value="enrouleur">🎯 Enrouleur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Débit (m³/h)</Label>
                    <Input
                      type="number"
                      className="h-9"
                      value={newReseau.debit_m3h}
                      onChange={(e) => setNewReseau(prev => ({ ...prev, debit_m3h: e.target.value }))}
                      placeholder="Ex: 10"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Pression (bar)</Label>
                    <Input
                      type="number"
                      className="h-9"
                      value={newReseau.pression_bar}
                      onChange={(e) => setNewReseau(prev => ({ ...prev, pression_bar: e.target.value }))}
                      placeholder="Ex: 3"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={newReseau.statut}
                  onValueChange={(v) => setNewReseau(prev => ({ ...prev, statut: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_place">✅ En place</SelectItem>
                    <SelectItem value="prevu">📋 Prévu</SelectItem>
                    <SelectItem value="a_faire">🔧 À faire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Diamètre (mm)</Label>
                <Input
                  type="number"
                  value={newReseau.diametre_mm}
                  onChange={(e) => setNewReseau(prev => ({ ...prev, diametre_mm: e.target.value }))}
                  placeholder="Ex: 25"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Matériau</Label>
              <Select
                value={newReseau.materiau}
                onValueChange={(v) => setNewReseau(prev => ({ ...prev, materiau: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pehd">PEHD</SelectItem>
                  <SelectItem value="pvc">PVC</SelectItem>
                  <SelectItem value="acier">Acier</SelectItem>
                  <SelectItem value="cuivre">Cuivre</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={newReseau.notes}
                onChange={(e) => setNewReseau(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Remarques..."
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowCreateReseauDialog(false);
              setPendingGeometry(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={createReseau}
              disabled={!newReseau.nom}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer le réseau
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog création intervention - FORMULAIRE COMPLET */}
      <Dialog open={showInterventionDialog} onOpenChange={setShowInterventionDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              Nouvelle Intervention
              {parcelleForIntervention && (
                <Badge variant="outline" className="ml-2">{parcelleForIntervention.nom}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Statut et Priorité */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut *</Label>
                <Select 
                  value={newIntervention.statut} 
                  onValueChange={(v) => setNewIntervention(prev => ({ ...prev, statut: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planifie">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        Planifié
                      </div>
                    </SelectItem>
                    <SelectItem value="en_cours">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-blue-500" />
                        En cours
                      </div>
                    </SelectItem>
                    <SelectItem value="termine">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Terminé
                      </div>
                    </SelectItem>
                    <SelectItem value="annule">
                      <div className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-500" />
                        Annulé
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select 
                  value={newIntervention.priorite} 
                  onValueChange={(v) => setNewIntervention(prev => ({ ...prev, priorite: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basse">Basse</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="haute">Haute</SelectItem>
                    <SelectItem value="urgente">🔴 Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Type d'intervention et Opération */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type d'intervention *</Label>
                <Select 
                  value={newIntervention.type_intervention} 
                  onValueChange={(v) => setNewIntervention(prev => ({ ...prev, type_intervention: v, operation: '' }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="travail_sol">🚜 Travail du sol</SelectItem>
                    <SelectItem value="semis">🌱 Semis</SelectItem>
                    <SelectItem value="traitement">💧 Traitement phyto</SelectItem>
                    <SelectItem value="fertilisation">🌿 Fertilisation</SelectItem>
                    <SelectItem value="irrigation">💦 Irrigation</SelectItem>
                    <SelectItem value="recolte">🌾 Récolte</SelectItem>
                    <SelectItem value="taille">✂️ Taille</SelectItem>
                    <SelectItem value="desherbage">🌿 Désherbage</SelectItem>
                    <SelectItem value="autre">📋 Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Opération détaillée</Label>
                <Input
                  value={newIntervention.operation}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, operation: e.target.value }))}
                  placeholder="Ex: Labour profond, Semis direct..."
                />
              </div>
            </div>
            
            {/* Dates et Heures */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Date début *</Label>
                <Input
                  type="date"
                  value={newIntervention.date_debut}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, date_debut: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <Input
                  type="date"
                  value={newIntervention.date_fin}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, date_fin: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Heure début</Label>
                <Input
                  type="time"
                  value={newIntervention.heure_debut}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, heure_debut: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Heure fin</Label>
                <Input
                  type="time"
                  value={newIntervention.heure_fin}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, heure_fin: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Surface, Durée prévisionnelle */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Surface traitée (ha)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newIntervention.surface_traitee}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, surface_traitee: e.target.value }))}
                  placeholder={parcelleForIntervention?.surface_ha || parcelleForIntervention?.surface || ''}
                />
              </div>
              <div className="space-y-2">
                <Label>Durée prévisionnelle (heures)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newIntervention.duree_previsionnelle}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, duree_previsionnelle: e.target.value }))}
                  placeholder="Ex: 2.5"
                />
              </div>
            </div>
            
            {/* Opérateur */}
            <div className="space-y-2">
              <Label>Opérateur</Label>
              {collaborateurs.length > 0 ? (
                <Select 
                  value={newIntervention.operateur_id} 
                  onValueChange={(v) => {
                    const collab = collaborateurs.find(c => c.id === v);
                    setNewIntervention(prev => ({ 
                      ...prev, 
                      operateur_id: v,
                      operateur_nom: collab ? `${collab.prenom || ''} ${collab.nom || ''}`.trim() : ''
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner un opérateur" /></SelectTrigger>
                  <SelectContent>
                    {collaborateurs.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.prenom} {c.nom} {c.role ? `(${c.role})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={newIntervention.operateur_nom}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, operateur_nom: e.target.value }))}
                  placeholder="Nom de l'opérateur"
                />
              )}
            </div>
            
            {/* Section Matériel utilisé - IDENTIQUE à InterventionsPage */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Tractor className="w-4 h-4" />
                  Matériel utilisé
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNewIntervention(prev => ({
                      ...prev,
                      materiels_selection: [...(prev.materiels_selection || []), { id: '', nom: '', searchQuery: '' }]
                    }));
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Matériel
                </Button>
              </div>
              
              {/* Liste des matériels sélectionnés */}
              {(newIntervention.materiels_selection || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-3 bg-muted/50 rounded">
                  Aucun matériel sélectionné. Cliquez sur "+ Matériel" pour en ajouter.
                </p>
              ) : (
                <div className="space-y-2">
                  {(newIntervention.materiels_selection || []).map((selection, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <Input
                          className="h-9 pr-8"
                          placeholder="Tapez pour rechercher un matériel..."
                          value={selection.searchQuery || selection.nom || ''}
                          onChange={(e) => {
                            const query = e.target.value;
                            setNewIntervention(prev => {
                              const updated = [...(prev.materiels_selection || [])];
                              updated[idx] = {
                                ...updated[idx],
                                searchQuery: query,
                                showSuggestions: true
                              };
                              // Si on efface, reset l'ID
                              if (!query) {
                                updated[idx].id = '';
                                updated[idx].nom = '';
                              }
                              return { ...prev, materiels_selection: updated };
                            });
                          }}
                          onFocus={() => {
                            setNewIntervention(prev => {
                              const updated = [...(prev.materiels_selection || [])];
                              updated[idx] = { ...updated[idx], showSuggestions: true };
                              return { ...prev, materiels_selection: updated };
                            });
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setNewIntervention(prev => {
                                const updated = [...(prev.materiels_selection || [])];
                                updated[idx] = { ...updated[idx], showSuggestions: false };
                                return { ...prev, materiels_selection: updated };
                              });
                            }, 200);
                          }}
                        />
                        {selection.id && (
                          <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                        )}
                        
                        {/* Suggestions de matériels */}
                        {selection.showSuggestions && (
                          <div className="absolute z-[300] w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {materielsListe
                              .filter(m => {
                                const query = (selection.searchQuery || '').toLowerCase();
                                if (!query) return true;
                                return m.nom?.toLowerCase().includes(query) || 
                                       m.type?.toLowerCase().includes(query) ||
                                       m.marque?.toLowerCase().includes(query);
                              })
                              .slice(0, 10)
                              .map((m) => (
                                <div
                                  key={m.id}
                                  className="px-3 py-2 hover:bg-accent cursor-pointer text-sm flex items-center justify-between"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setNewIntervention(prev => {
                                      const updated = [...(prev.materiels_selection || [])];
                                      updated[idx] = {
                                        id: m.id,
                                        nom: m.nom,
                                        type: m.type,
                                        searchQuery: m.nom,
                                        showSuggestions: false
                                      };
                                      return { ...prev, materiels_selection: updated };
                                    });
                                  }}
                                >
                                  <div>
                                    <span className="font-medium">{m.nom}</span>
                                    {m.type && <span className="text-xs text-muted-foreground ml-2">({m.type})</span>}
                                  </div>
                                  {m.marque && <span className="text-xs text-muted-foreground">{m.marque}</span>}
                                </div>
                              ))
                            }
                            {materielsListe.filter(m => {
                              const query = (selection.searchQuery || '').toLowerCase();
                              if (!query) return true;
                              return m.nom?.toLowerCase().includes(query) || m.type?.toLowerCase().includes(query);
                            }).length === 0 && (
                              <p className="px-3 py-2 text-sm text-muted-foreground">Aucun matériel trouvé</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Bouton supprimer */}
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setNewIntervention(prev => ({
                            ...prev,
                            materiels_selection: (prev.materiels_selection || []).filter((_, i) => i !== idx)
                          }));
                        }}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Résumé des matériels sélectionnés */}
              {(newIntervention.materiels_selection || []).filter(s => s.id).length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {(newIntervention.materiels_selection || []).filter(s => s.id).map((s, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      <Tractor className="w-3 h-3 mr-1" />
                      {s.nom}
                      {s.type && <span className="opacity-70 ml-1">({s.type})</span>}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* Section Traitement Phyto - Paramètres de pulvérisation */}
            {(newIntervention.type_intervention === 'traitement' || newIntervention.operation?.toLowerCase().includes('traitement') || newIntervention.operation?.toLowerCase().includes('phyto')) && (
              <div className="space-y-3 border-t pt-4 bg-blue-50/50 dark:bg-blue-950/20 -mx-6 px-6 py-4">
                <Label className="text-base font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <Droplet className="w-4 h-4" />
                  Paramètres de pulvérisation
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Volume bouillie (L/ha) *</Label>
                    <Input
                      type="number"
                      value={newIntervention.volume_bouillie_ha}
                      onChange={(e) => setNewIntervention(prev => ({ ...prev, volume_bouillie_ha: e.target.value }))}
                      placeholder="Ex: 200"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Pression (bars)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={newIntervention.pression_bars}
                      onChange={(e) => setNewIntervention(prev => ({ ...prev, pression_bars: e.target.value }))}
                      placeholder="Ex: 3"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Vitesse (km/h)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={newIntervention.vitesse_avancement}
                      onChange={(e) => setNewIntervention(prev => ({ ...prev, vitesse_avancement: e.target.value }))}
                      placeholder="Ex: 6"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type de buse</Label>
                    <Select 
                      value={newIntervention.type_buse}
                      onValueChange={(v) => setNewIntervention(prev => ({ ...prev, type_buse: v }))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fente_110">Fente 110°</SelectItem>
                        <SelectItem value="fente_80">Fente 80°</SelectItem>
                        <SelectItem value="miroir">Miroir</SelectItem>
                        <SelectItem value="anti_derive">Anti-dérive</SelectItem>
                        <SelectItem value="injection">Injection air</SelectItem>
                        <SelectItem value="centrifuge">Centrifuge</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Alerte si volume non renseigné */}
                {!newIntervention.volume_bouillie_ha && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-3 py-2 rounded">
                    <AlertCircle className="w-4 h-4" />
                    Le volume de bouillie/ha est requis pour le registre phytosanitaire
                  </div>
                )}
              </div>
            )}
            
            {/* Section Cibles et Suggestions IA (si traitement) */}
            {newIntervention.type_intervention === 'traitement' && (
              <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-lg space-y-3 border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold flex items-center gap-2">
                      🎯 Cibles de traitement
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ajoutez les cibles pour obtenir des suggestions de produits homologués pour <span className="font-medium text-green-700">{parcelleForIntervention?.culture_actuelle || parcelleForIntervention?.culture || parcelleForIntervention?.culture_nom || 'cette culture'}</span>
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCibles(prev => [...prev, { id: Date.now(), nom: '', type: 'ravageur' }])}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter cible
                  </Button>
                </div>
                
                {/* Liste des cibles */}
                {cibles.length === 0 ? (
                  <div className="text-center py-3 bg-white/50 dark:bg-black/20 rounded border-2 border-dashed border-green-300">
                    <p className="text-sm text-muted-foreground">
                      Aucune cible définie - Ajoutez une cible pour voir les suggestions de produits
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {cibles.map((cible, idx) => (
                      <div key={cible.id} className="flex items-center gap-2">
                        <Select
                          value={cible.type}
                          onValueChange={(v) => {
                            const updated = [...cibles];
                            updated[idx].type = v;
                            setCibles(updated);
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ravageur">🐛 Ravageur</SelectItem>
                            <SelectItem value="maladie">🦠 Maladie</SelectItem>
                            <SelectItem value="adventice">🌿 Adventice</SelectItem>
                            <SelectItem value="carence">⚠️ Carence</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          className="flex-1"
                          placeholder={cible.type === 'ravageur' ? 'Ex: Mildiou, Oïdium, Puceron...' : 
                                      cible.type === 'maladie' ? 'Ex: Black rot, Botrytis...' :
                                      cible.type === 'adventice' ? 'Ex: Chiendent, Liseron...' :
                                      'Ex: Azote, Fer, Magnésium...'}
                          value={cible.nom}
                          onChange={(e) => {
                            const updated = [...cibles];
                            updated[idx].nom = e.target.value;
                            setCibles(updated);
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setCibles(prev => prev.filter((_, i) => i !== idx))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Bouton recherche suggestions IA */}
                {cibles.length > 0 && cibles.some(c => c.nom?.length >= 2) && (
                  <div className="pt-2 border-t border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">💡</span>
                        <span className="font-semibold text-green-800 dark:text-green-200">Suggestions IA</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={loadingSuggestionsIA}
                        onClick={async () => {
                          setLoadingSuggestionsIA(true);
                          try {
                            const culture = parcelleForIntervention?.culture_actuelle || 
                                           parcelleForIntervention?.culture || 
                                           parcelleForIntervention?.culture_nom || '';
                            
                            // Vérifier si la parcelle est en BIO
                            const is_bio = parcelleForIntervention?.is_bio || 
                                          parcelleForIntervention?.mode_culture === 'bio' || 
                                          parcelleForIntervention?.mode_culture === 'conversion' ||
                                          false;
                            
                            // Calculer les jours avant récolte si date de récolte prévue
                            let jours_avant_recolte = null;
                            if (parcelleForIntervention?.date_recolte_prevue) {
                              const dateRecolte = new Date(parcelleForIntervention.date_recolte_prevue);
                              const today = new Date();
                              jours_avant_recolte = Math.ceil((dateRecolte - today) / (1000 * 60 * 60 * 24));
                              if (jours_avant_recolte < 0) jours_avant_recolte = null;
                            }
                            
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${API_URL}/api/registre-phyto/suggestions`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                culture,
                                cibles: cibles.filter(c => c.nom),
                                type_traitement: newIntervention.type_intervention,
                                is_bio: is_bio,
                                jours_avant_recolte: jours_avant_recolte,
                                entree_ouvriers_prevue: false
                              })
                            });
                            
                            if (res.ok) {
                              const data = await res.json();
                              setSuggestionsProduitsIA(data.suggestions || []);
                              // Afficher message de priorité ou mode bio
                              if (data.message) {
                                if (data.priorite_casia) {
                                  toast.success(data.message, { duration: 5000 });
                                } else {
                                  toast.info(data.message);
                                }
                              }
                              // Afficher info CASIA si applicable
                              if (data.info_casia) {
                                toast.info(data.info_casia, { duration: 4000 });
                              }
                            }
                          } catch (err) {
                            console.error('Erreur suggestions IA:', err);
                          }
                          setLoadingSuggestionsIA(false);
                        }}
                      >
                        {loadingSuggestionsIA ? (
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Search className="w-4 h-4 mr-2" />
                        )}
                        Rechercher produits homologués
                      </Button>
                    </div>
                    
                    {/* Indicateur mode BIO */}
                    {(parcelleForIntervention?.is_bio || parcelleForIntervention?.mode_culture === 'bio' || parcelleForIntervention?.mode_culture === 'conversion') && (
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded border border-green-300 mb-2">
                        <p className="text-sm text-green-800 dark:text-green-200 flex items-center gap-2">
                          <span>🌿</span>
                          <span className="font-medium">Parcelle en Agriculture Biologique</span>
                          <span className="text-xs">- Seuls les produits autorisés en BIO sont proposés</span>
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs text-green-700 dark:text-green-300 mb-2">
                      Basé sur : {cibles.filter(c => c.nom).map(c => c.nom).join(', ')} 
                      {(parcelleForIntervention?.culture_actuelle || parcelleForIntervention?.culture) && 
                        ` • Culture: ${parcelleForIntervention?.culture_actuelle || parcelleForIntervention?.culture || parcelleForIntervention?.culture_nom}`}
                    </p>
                    
                    {/* Liste des suggestions - IDENTIQUE À INTERVENTIONSPAGE */}
                    {suggestionsProduitsIA.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {suggestionsProduitsIA.map((sugg, idx) => (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-md border cursor-pointer hover:bg-white/50 dark:hover:bg-black/20 transition-colors ${
                              sugg.homologue === false 
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-300' 
                                : sugg.priorite_absolue
                                  ? 'bg-emerald-200/70 dark:bg-emerald-900/40 border-emerald-500 ring-2 ring-emerald-400'
                                  : sugg.is_casia 
                                    ? 'bg-emerald-100/50 dark:bg-emerald-900/20 border-emerald-300' 
                                    : 'bg-white/30 border-gray-200'
                            }`}
                            onClick={() => {
                              // Sélectionner ce produit
                              setNewIntervention(prev => ({
                                ...prev,
                                produit_nom: sugg.nom,
                                numero_amm: sugg.numero_amm,
                                dose: sugg.dose_recommandee || '',
                                unite_dose: sugg.unite || 'L/ha'
                              }));
                              setProduitSearch(sugg.nom);
                              setSelectedProduitEphy({
                                nom: sugg.nom,
                                numero_amm: sugg.numero_amm,
                                firme: sugg.firme,
                                homologue: sugg.homologue,
                                dose_recommandee: sugg.dose_recommandee,
                                dose_max: sugg.dose_max,
                                is_casia: sugg.is_casia,
                                dar: sugg.dar,
                                dre: sugg.dre
                              });
                              toast.success(`${sugg.nom} sélectionné`);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                {sugg.priorite_absolue && (
                                  <Badge className="bg-emerald-700 text-white text-xs animate-pulse">⭐ PRIORITÉ</Badge>
                                )}
                                {sugg.is_casia && (
                                  <Badge className="bg-emerald-600 text-white text-xs">CASIA</Badge>
                                )}
                                {sugg.is_pnpp && (
                                  <Badge className="bg-teal-600 text-white text-xs">PNPP</Badge>
                                )}
                                {sugg.is_bio && (
                                  <Badge className="bg-green-700 text-white text-xs">🌿 BIO</Badge>
                                )}
                                {sugg.dar === 0 && (
                                  <Badge className="bg-blue-600 text-white text-xs">DAR 0</Badge>
                                )}
                                {sugg.dre === 0 && (
                                  <Badge className="bg-purple-600 text-white text-xs">DRE 0</Badge>
                                )}
                                {sugg.homologue === true && !sugg.is_casia && (
                                  <Badge className="bg-green-600 text-white text-xs">✓ Homologué</Badge>
                                )}
                                {sugg.homologue === false && (
                                  <Badge className="bg-red-600 text-white text-xs">⚠ Non homologué</Badge>
                                )}
                                <span className="font-medium text-sm">{sugg.nom}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {sugg.dose_recommandee} {sugg.unite || 'L/ha'}
                                </Badge>
                                {/* Bouton calculette */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowCalculette({
                                      produit: {
                                        nom: sugg.nom,
                                        numero_amm: sugg.numero_amm,
                                        firme: sugg.firme,
                                        dose_recommandee: sugg.dose_recommandee,
                                        dose_max: sugg.dose_max,
                                        unite: sugg.unite || 'L/ha'
                                      },
                                      volumeBouillie: newIntervention.volume_bouillie_ha || '200'
                                    });
                                  }}
                                  title="Ouvrir la calculette"
                                >
                                  <Calculator className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            {/* Message avantage CASIA */}
                            {sugg.message_avantage && (
                              <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium mt-1 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-1 rounded">
                                {sugg.message_avantage}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-1">
                              {sugg.firme && (
                                <p className="text-xs text-muted-foreground">{sugg.firme} • AMM: {sugg.numero_amm}</p>
                              )}
                              {sugg.homologation_raison && (
                                <p className={`text-xs ${sugg.homologue === false ? 'text-red-600 font-medium' : 'text-green-600'}`}>
                                  {sugg.homologation_raison}
                                </p>
                              )}
                            </div>
                            {/* Info DAR/DRE pour produits non-CASIA */}
                            {!sugg.is_casia && (sugg.dar > 0 || sugg.dre > 0) && (
                              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                ⚠️ DAR: {sugg.dar}j • DRE: {sugg.dre}h
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        Cliquez sur "Rechercher produits homologués" pour obtenir des suggestions
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* ============ SECTION FERTILISATION IA ============ */}
            {newIntervention.type_intervention === 'fertilisation' && (
              <div className="p-4 bg-gradient-to-r from-amber-50 to-green-50 dark:from-amber-950/30 dark:to-green-950/30 rounded-lg space-y-4 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-lg font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-300">
                      <Leaf className="w-5 h-5" />
                      Fertilisation - Cahier d'épandage
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Culture : <span className="font-medium text-green-700">{parcelleForIntervention?.culture_actuelle || parcelleForIntervention?.culture || 'Non définie'}</span> • 
                      Surface : <span className="font-medium">{parcelleForIntervention?.surface_ha || newIntervention.surface_travaillee || '?'} ha</span>
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="gap-2 bg-amber-600 hover:bg-amber-700"
                    onClick={async () => {
                      try {
                        const response = await api.post('/fertilisation/suggestion-ia', {
                          objectif: newIntervention.objectif_fertilisation || 'fumure_croissance',
                          culture: parcelleForIntervention?.culture_actuelle || parcelleForIntervention?.culture || 'vigne',
                          surface_ha: parseFloat(parcelleForIntervention?.surface_ha || newIntervention.surface_travaillee || 10),
                          mode_production: parcelleForIntervention?.mode_production || 'conventionnel'
                        });
                        if (response.data?.suggestions) {
                          setNewIntervention(prev => ({
                            ...prev,
                            suggestions_fertilisation: response.data.suggestions,
                            besoins_culture: response.data.besoins_culture,
                            alertes_fertilisation: response.data.alertes,
                            conseils_fertilisation: response.data.conseils
                          }));
                          toast.success('Suggestions IA récupérées !');
                        }
                      } catch (error) {
                        toast.error('Erreur lors de la suggestion');
                      }
                    }}
                    data-testid="btn-suggestion-fertilisation"
                  >
                    <Sparkles className="w-4 h-4" />
                    Suggestion IA
                  </Button>
                </div>
                
                {/* Choix de l'objectif de fertilisation */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'fumure_fond', label: 'Fumure de fond', icon: '🏔️', desc: 'Enrichir le sol' },
                    { id: 'fumure_croissance', label: 'Fumure croissance', icon: '🌱', desc: 'Faire pousser' },
                    { id: 'engrais_foliaire', label: 'Engrais foliaire', icon: '🍃', desc: 'Correction rapide' }
                  ].map(obj => (
                    <button
                      key={obj.id}
                      type="button"
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        (newIntervention.objectif_fertilisation || 'fumure_croissance') === obj.id
                          ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/50'
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                      onClick={() => setNewIntervention(prev => ({ ...prev, objectif_fertilisation: obj.id }))}
                    >
                      <span className="text-2xl">{obj.icon}</span>
                      <p className="font-medium text-sm mt-1">{obj.label}</p>
                      <p className="text-xs text-muted-foreground">{obj.desc}</p>
                    </button>
                  ))}
                </div>
                
                {/* Affichage des suggestions IA */}
                {newIntervention.suggestions_fertilisation?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Produits recommandés
                    </Label>
                    <div className="space-y-2">
                      {newIntervention.suggestions_fertilisation.map((sugg, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            sugg.marque === 'CASIA' 
                              ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100' 
                              : 'bg-white hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            // Ajouter le produit aux produits de l'intervention
                            setNewIntervention(prev => ({
                              ...prev,
                              produits: [...(prev.produits || []), {
                                id: Date.now(),
                                type: 'fertilisant',
                                nom: sugg.nom,
                                quantite: sugg.dose_preconisee,
                                unite: sugg.unite,
                                is_casia: sugg.marque === 'CASIA',
                                justification: sugg.justification
                              }]
                            }));
                            toast.success(`${sugg.nom} ajouté !`);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {sugg.marque === 'CASIA' && (
                                <Badge className="bg-emerald-600 text-white text-xs">CASIA</Badge>
                              )}
                              <span className="font-medium">{sugg.nom}</span>
                            </div>
                            <Badge variant="outline" className="text-amber-700 border-amber-300">
                              {sugg.dose_preconisee} {sugg.unite}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{sugg.justification}</p>
                          {sugg.periode_ideale && (
                            <p className="text-xs text-blue-600 mt-1">📅 {sugg.periode_ideale}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Alertes et conseils */}
                {newIntervention.alertes_fertilisation?.length > 0 && (
                  <div className="p-2 bg-amber-100 rounded text-sm">
                    {newIntervention.alertes_fertilisation.map((a, i) => (
                      <p key={i} className="text-amber-800">{a}</p>
                    ))}
                  </div>
                )}
                
                {/* Saisie manuelle des éléments fertilisants */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-2 border-t">
                  <div>
                    <Label className="text-xs">N (kg/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-sm"
                      placeholder="0"
                      value={newIntervention.fertilisation?.azote_total_kg || ''}
                      onChange={(e) => setNewIntervention(prev => ({
                        ...prev,
                        fertilisation: { ...(prev.fertilisation || {}), azote_total_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">P₂O₅ (kg/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-sm"
                      placeholder="0"
                      value={newIntervention.fertilisation?.phosphore_p2o5_kg || ''}
                      onChange={(e) => setNewIntervention(prev => ({
                        ...prev,
                        fertilisation: { ...(prev.fertilisation || {}), phosphore_p2o5_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">K₂O (kg/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-sm"
                      placeholder="0"
                      value={newIntervention.fertilisation?.potasse_k2o_kg || ''}
                      onChange={(e) => setNewIntervention(prev => ({
                        ...prev,
                        fertilisation: { ...(prev.fertilisation || {}), potasse_k2o_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">MgO (kg/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-sm"
                      placeholder="0"
                      value={newIntervention.fertilisation?.magnesie_mgo_kg || ''}
                      onChange={(e) => setNewIntervention(prev => ({
                        ...prev,
                        fertilisation: { ...(prev.fertilisation || {}), magnesie_mgo_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">MO (t/ha)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      className="h-8 text-sm"
                      placeholder="0"
                      value={newIntervention.fertilisation?.matiere_organique_t || ''}
                      onChange={(e) => setNewIntervention(prev => ({
                        ...prev,
                        fertilisation: { ...(prev.fertilisation || {}), matiere_organique_t: e.target.value }
                      }))}
                    />
                  </div>
                </div>
                
                {/* Résumé quantités totales */}
                {(newIntervention.fertilisation?.azote_total_kg || newIntervention.fertilisation?.phosphore_p2o5_kg) && (newIntervention.surface_travaillee || parcelleForIntervention?.surface_ha) && (
                  <div className="p-3 bg-white rounded-lg border">
                    <Label className="text-xs font-semibold text-amber-700 mb-2 block">
                      🚚 Quantités totales à commander ({newIntervention.surface_travaillee || parcelleForIntervention?.surface_ha} ha)
                    </Label>
                    <div className="flex flex-wrap gap-3">
                      {newIntervention.fertilisation?.azote_total_kg && (
                        <div className="bg-blue-100 px-3 py-1 rounded">
                          <span className="font-bold text-blue-800">
                            {Math.round(parseFloat(newIntervention.fertilisation.azote_total_kg) * parseFloat(newIntervention.surface_travaillee || parcelleForIntervention?.surface_ha || 1))} kg
                          </span>
                          <span className="text-xs text-blue-600 ml-1">N total</span>
                        </div>
                      )}
                      {newIntervention.fertilisation?.phosphore_p2o5_kg && (
                        <div className="bg-orange-100 px-3 py-1 rounded">
                          <span className="font-bold text-orange-800">
                            {Math.round(parseFloat(newIntervention.fertilisation.phosphore_p2o5_kg) * parseFloat(newIntervention.surface_travaillee || parcelleForIntervention?.surface_ha || 1))} kg
                          </span>
                          <span className="text-xs text-orange-600 ml-1">P₂O₅</span>
                        </div>
                      )}
                      {newIntervention.fertilisation?.potasse_k2o_kg && (
                        <div className="bg-purple-100 px-3 py-1 rounded">
                          <span className="font-bold text-purple-800">
                            {Math.round(parseFloat(newIntervention.fertilisation.potasse_k2o_kg) * parseFloat(newIntervention.surface_travaillee || parcelleForIntervention?.surface_ha || 1))} kg
                          </span>
                          <span className="text-xs text-purple-600 ml-1">K₂O</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Section Produits utilisés (EPHY/CASIA) - IDENTIQUE à InterventionsPage */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-semibold">Produits utilisés (EPHY/CASIA)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewIntervention(prev => ({
                    ...prev,
                    produits: [...(prev.produits || []), {
                      id: Date.now(),
                      type: 'phyto',
                      nom: '',
                      fournisseur: '',
                      quantite: '',
                      unite: 'L/ha',
                      cout_unitaire: '',
                      cout_total: '',
                      numero_amm: '',
                      firme: '',
                      dose_max: null,
                      dose_recommandee: null,
                      cultures_autorisees: [],
                      contient_cuivre: false,
                      cuivre_kg_ha: null,
                      alerte: null
                    }]
                  }))}
                  data-testid="btn-ajouter-produit"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter produit
                </Button>
              </div>
              
              {(newIntervention.produits || []).length === 0 ? (
                <div className="text-center py-6 bg-muted/50 rounded-lg border-2 border-dashed">
                  <Leaf className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Aucun produit ajouté
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Recherchez dans la base officielle EPHY/CASIA pour des recommandations de doses
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(newIntervention.produits || []).map((produit, idx) => {
                    const PICTO_LABELS = {
                      'GHS01': { emoji: '💥', label: 'Explosif' },
                      'GHS02': { emoji: '🔥', label: 'Inflammable' },
                      'GHS03': { emoji: '⭕', label: 'Comburant' },
                      'GHS04': { emoji: '🔵', label: 'Gaz sous pression' },
                      'GHS05': { emoji: '⚗️', label: 'Corrosif' },
                      'GHS06': { emoji: '☠️', label: 'Toxique' },
                      'GHS07': { emoji: '⚠️', label: 'Attention' },
                      'GHS08': { emoji: '🫁', label: 'Danger santé' },
                      'GHS09': { emoji: '🐟', label: 'Environnement' },
                    };
                    
                    return (
                      <Card key={produit.id} className={`p-3 ${produit.alerte ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20' : ''}`}>
                        {/* Alertes IA */}
                        {produit.alerte && (
                          <div className={`mb-3 p-2 rounded-md text-xs flex items-start gap-2 ${
                            produit.alerte.type === 'danger' ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200' :
                            produit.alerte.type === 'warning' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200' :
                            'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
                          }`}>
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold">{produit.alerte.titre}</p>
                              <p>{produit.alerte.message}</p>
                            </div>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                          {/* Type */}
                          <div className="col-span-2 md:col-span-1">
                            <Label className="text-xs">Type</Label>
                            <Select
                              value={produit.type}
                              onValueChange={(v) => {
                                setNewIntervention(prev => {
                                  const updated = [...(prev.produits || [])];
                                  updated[idx].type = v;
                                  return { ...prev, produits: updated };
                                });
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="intrant">🌱 Intrant</SelectItem>
                                <SelectItem value="phyto">🧪 Phyto</SelectItem>
                                <SelectItem value="engrais">💧 Engrais</SelectItem>
                                <SelectItem value="semence">🌾 Semence</SelectItem>
                                <SelectItem value="divers">📦 Divers</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Nom produit avec recherche EPHY/CASIA */}
                          <div className="col-span-2 md:col-span-2 relative">
                            <Label className="text-xs">Nom du produit (EPHY/CASIA)</Label>
                            <div className="relative">
                              <Input
                                className={`h-8 text-xs pr-8 ${!produit.numero_amm && produit.nom?.length > 3 ? 'border-amber-400' : ''}`}
                                placeholder="Tapez pour rechercher dans EPHY..."
                                value={produit.nom}
                                onChange={async (e) => {
                                  const value = e.target.value;
                                  
                                  setNewIntervention(prev => {
                                    const updated = [...(prev.produits || [])];
                                    updated[idx] = {
                                      ...updated[idx],
                                      nom: value,
                                      showSuggestions: true,
                                      searching: value.length >= 2,
                                      suggestions: value.length < 2 ? [] : updated[idx].suggestions
                                    };
                                    return { ...prev, produits: updated };
                                  });
                                  
                                  if (value.length >= 2) {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const res = await fetch(`${API_URL}/api/registre-phyto/ephy/search?q=${encodeURIComponent(value)}&limit=10`, {
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (res.ok) {
                                        const data = await res.json();
                                        setNewIntervention(prev => {
                                          const updated = [...(prev.produits || [])];
                                          updated[idx] = {
                                            ...updated[idx],
                                            suggestions: data.produits || [],
                                            searching: false
                                          };
                                          return { ...prev, produits: updated };
                                        });
                                      }
                                    } catch (err) {
                                      console.log('Recherche EPHY:', err);
                                      setNewIntervention(prev => {
                                        const updated = [...(prev.produits || [])];
                                        updated[idx] = { ...updated[idx], searching: false, suggestions: [] };
                                        return { ...prev, produits: updated };
                                      });
                                    }
                                  }
                                }}
                                onFocus={() => {
                                  setNewIntervention(prev => {
                                    const updated = [...(prev.produits || [])];
                                    updated[idx] = { ...updated[idx], showSuggestions: true };
                                    return { ...prev, produits: updated };
                                  });
                                }}
                                onBlur={() => {
                                  setTimeout(() => {
                                    setNewIntervention(prev => {
                                      const updated = [...(prev.produits || [])];
                                      updated[idx] = { ...updated[idx], showSuggestions: false };
                                      if (updated[idx].nom?.length > 3 && !updated[idx].numero_amm) {
                                        updated[idx].alerte = {
                                          type: 'warning',
                                          titre: '⚠️ PRODUIT NON HOMOLOGUÉ / NON RÉFÉRENCÉ',
                                          message: `"${updated[idx].nom}" n'est pas trouvé dans la base EPHY. Vérifiez l'orthographe.`
                                        };
                                      }
                                      return { ...prev, produits: updated };
                                    });
                                  }, 300);
                                }}
                              />
                              {produit.searching && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                              
                              {/* Liste des suggestions EPHY */}
                              {produit.showSuggestions && Array.isArray(produit.suggestions) && produit.suggestions.length > 0 && (
                                <div className="absolute z-[300] w-full mt-1 bg-popover border rounded-md shadow-xl max-h-64 overflow-y-auto">
                                  <div className="px-3 py-1.5 bg-muted/50 text-xs text-muted-foreground border-b sticky top-0 z-10 bg-background">
                                    🔍 {produit.suggestions.length} produits trouvés dans EPHY
                                  </div>
                                  {produit.suggestions.map((suggestion, sIdx) => {
                                    const contientCuivre = suggestion.nom?.toLowerCase().includes('cuivre') || 
                                                           suggestion.substances_actives?.toLowerCase().includes('cuivre');
                                    
                                    return (
                                      <div
                                        key={sIdx}
                                        className="px-3 py-2 hover:bg-accent cursor-pointer text-xs border-b last:border-0 transition-colors"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setNewIntervention(prev => {
                                            const updated = [...(prev.produits || [])];
                                            updated[idx] = {
                                              ...updated[idx],
                                              nom: suggestion.nom,
                                              numero_amm: suggestion.numero_amm,
                                              firme: suggestion.firme,
                                              type: suggestion.type_produit?.includes('bio') ? 'intrant' : 'phyto',
                                              showSuggestions: false,
                                              suggestions: [],
                                              contient_cuivre: contientCuivre,
                                              dose_max: suggestion.dose_max || (contientCuivre ? 4 : 10),
                                              dose_recommandee: suggestion.dose_max ? (suggestion.dose_max * 0.8).toFixed(1) : null,
                                              unite: suggestion.unite_dose || 'L/ha',
                                              substances_actives: suggestion.substances_actives,
                                              cultures_autorisees: suggestion.cultures || [],
                                              pictos_sgh: suggestion.pictos_sgh,
                                              url_fiche_ephy: suggestion.url_fiche_ephy,
                                              alerte: contientCuivre ? {
                                                type: 'info',
                                                titre: '🟢 Produit à base de cuivre - Homologué',
                                                message: `AMM: ${suggestion.numero_amm}. Limite UE: 4 kg Cu/ha/an. Dose max: ${suggestion.dose_max || 4} ${suggestion.unite_dose || 'L/ha'}`
                                              } : {
                                                type: 'info',
                                                titre: '✅ Produit homologué EPHY',
                                                message: `AMM: ${suggestion.numero_amm}${suggestion.firme ? ` - ${suggestion.firme}` : ''}`
                                              }
                                            };
                                            return { ...prev, produits: updated };
                                          });
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className="font-semibold text-sm">{suggestion.nom}</span>
                                          <div className="flex items-center gap-1">
                                            {suggestion.pictos_sgh?.map((picto, pi) => (
                                              <span key={pi} title={PICTO_LABELS[picto]?.label || picto} className="text-base">
                                                {PICTO_LABELS[picto]?.emoji || '⚠️'}
                                              </span>
                                            ))}
                                            {contientCuivre && (
                                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-orange-100 text-orange-700 border-orange-300">
                                                Cu
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                        {suggestion.substances_actives && (
                                          <div className="bg-blue-50 dark:bg-blue-950/30 rounded px-2 py-1 mt-1">
                                            <p className="text-[11px] font-medium text-blue-700 dark:text-blue-300">
                                              🧬 {suggestion.substances_actives}
                                            </p>
                                          </div>
                                        )}
                                        <div className="flex items-center justify-between mt-1.5">
                                          <span className="text-green-600 font-medium text-[10px]">✓ AMM: {suggestion.numero_amm}</span>
                                          {suggestion.dose_max && (
                                            <Badge className="text-[10px] py-0 bg-primary/20 text-primary">
                                              Max {suggestion.dose_max} {suggestion.unite_dose || 'L/ha'}
                                            </Badge>
                                          )}
                                        </div>
                                        <span className="text-[10px] text-muted-foreground">{suggestion.firme}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            
                            {produit.numero_amm && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-green-600 font-medium">✓ AMM: {produit.numero_amm}</span>
                                {produit.dose_recommandee && (
                                  <span className="text-[10px] text-blue-600">📋 Dose max: {produit.dose_max} {produit.unite || 'L/ha'}</span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Fournisseur */}
                          <div className="col-span-2 md:col-span-1">
                            <Label className="text-xs">Fournisseur</Label>
                            <Input
                              className="h-8 text-xs"
                              placeholder="Coop, Négoce..."
                              value={produit.fournisseur}
                              onChange={(e) => {
                                setNewIntervention(prev => {
                                  const updated = [...(prev.produits || [])];
                                  updated[idx].fournisseur = e.target.value;
                                  return { ...prev, produits: updated };
                                });
                              }}
                            />
                          </div>
                          
                          {/* Quantité + Unité */}
                          <div className="flex gap-1">
                            <div className="flex-1">
                              <Label className="text-xs">Dose/ha</Label>
                              <Input
                                type="number"
                                step="0.1"
                                className={`h-8 text-xs ${produit.dose_depassee ? 'border-red-500 bg-red-50' : ''}`}
                                placeholder="Ex: 2.5"
                                value={produit.quantite}
                                onChange={(e) => {
                                  const dose = parseFloat(e.target.value) || 0;
                                  setNewIntervention(prev => {
                                    const updated = [...(prev.produits || [])];
                                    updated[idx].quantite = e.target.value;
                                    
                                    if (updated[idx].dose_max && dose > updated[idx].dose_max) {
                                      updated[idx].dose_depassee = true;
                                      updated[idx].alerte = {
                                        type: 'danger',
                                        titre: '🔴 DOSE MAXIMALE DÉPASSÉE',
                                        message: `La dose saisie (${dose} ${updated[idx].unite || 'L/ha'}) dépasse la dose homologuée (${updated[idx].dose_max} ${updated[idx].unite || 'L/ha'}).`
                                      };
                                    } else if (updated[idx].contient_cuivre && dose > 4) {
                                      updated[idx].dose_depassee = true;
                                      updated[idx].alerte = {
                                        type: 'warning',
                                        titre: '🟠 Limite cuivre annuelle',
                                        message: `Attention: ${dose} kg Cu/ha dépasse la limite UE de 4 kg Cu/ha/an.`
                                      };
                                    } else {
                                      updated[idx].dose_depassee = false;
                                      if (updated[idx].contient_cuivre) {
                                        updated[idx].alerte = { type: 'info', titre: '🟢 Produit à base de cuivre', message: 'Dose OK. Limite UE: 4 kg Cu/ha/an.' };
                                      } else if (updated[idx].numero_amm) {
                                        updated[idx].alerte = null;
                                      }
                                    }
                                    
                                    if (updated[idx].cout_unitaire) {
                                      updated[idx].cout_total = (dose * parseFloat(updated[idx].cout_unitaire)).toFixed(2);
                                    }
                                    return { ...prev, produits: updated };
                                  });
                                }}
                              />
                              {produit.dose_depassee && (
                                <p className="text-[10px] text-red-600 mt-0.5 font-semibold">⚠️ Max: {produit.dose_max}</p>
                              )}
                            </div>
                            <div className="w-20">
                              <Label className="text-xs">Unité</Label>
                              <Select
                                value={produit.unite}
                                onValueChange={(v) => {
                                  setNewIntervention(prev => {
                                    const updated = [...(prev.produits || [])];
                                    updated[idx].unite = v;
                                    return { ...prev, produits: updated };
                                  });
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="L/ha">L/ha</SelectItem>
                                  <SelectItem value="kg/ha">kg/ha</SelectItem>
                                  <SelectItem value="g/ha">g/ha</SelectItem>
                                  <SelectItem value="mL/hL">mL/hL</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {/* Coût unitaire */}
                          <div>
                            <Label className="text-xs">€/unité</Label>
                            <Input
                              type="number"
                              step="0.01"
                              className="h-8 text-xs"
                              placeholder="5.50"
                              value={produit.cout_unitaire}
                              onChange={(e) => {
                                setNewIntervention(prev => {
                                  const updated = [...(prev.produits || [])];
                                  updated[idx].cout_unitaire = e.target.value;
                                  if (updated[idx].quantite) {
                                    updated[idx].cout_total = (parseFloat(updated[idx].quantite) * parseFloat(e.target.value)).toFixed(2);
                                  }
                                  return { ...prev, produits: updated };
                                });
                              }}
                            />
                          </div>
                          
                          {/* Coût total + Calculette + Supprimer */}
                          <div className="flex items-end gap-1">
                            <div className="flex-1">
                              <Label className="text-xs">Total €</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="h-8 text-xs bg-muted"
                                value={produit.cout_total}
                                readOnly
                              />
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 bg-primary/10 hover:bg-primary/20 text-primary"
                              onClick={() => setShowCalculette({ produit, idx })}
                              title="Calculette pulvé"
                            >
                              <Calculator className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => {
                                setNewIntervention(prev => ({
                                  ...prev,
                                  produits: (prev.produits || []).filter((_, i) => i !== idx)
                                }));
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                  
                  {/* Résumé des coûts */}
                  {(newIntervention.produits || []).length > 0 && (
                    <div className="flex justify-end">
                      <div className="bg-primary/10 rounded-lg px-4 py-2">
                        <span className="text-sm text-muted-foreground mr-2">Coût total produits :</span>
                        <span className="font-bold text-lg">
                          {(newIntervention.produits || []).reduce((sum, p) => sum + (parseFloat(p.cout_total) || 0), 0).toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Calculette Produit - Modal */}
            {showCalculette && (
              <CalculetteProduit
                produit={showCalculette.produit}
                volumeBouillie={newIntervention.volume_bouillie_ha}
                onClose={() => setShowCalculette(null)}
              />
            )}
            
            {/* Météo - IDENTIQUE à InterventionsPage */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Météo</Label>
                <Select 
                  value={newIntervention.meteo} 
                  onValueChange={(v) => setNewIntervention(prev => ({ ...prev, meteo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Conditions météo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ensoleille">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4 text-yellow-500" />
                        Ensoleillé
                      </div>
                    </SelectItem>
                    <SelectItem value="nuageux">
                      <div className="flex items-center gap-2">
                        <Cloud className="w-4 h-4 text-gray-500" />
                        Nuageux
                      </div>
                    </SelectItem>
                    <SelectItem value="pluie">
                      <div className="flex items-center gap-2">
                        <CloudRain className="w-4 h-4 text-blue-500" />
                        Pluie
                      </div>
                    </SelectItem>
                    <SelectItem value="vent">
                      <div className="flex items-center gap-2">
                        <Wind className="w-4 h-4 text-teal-500" />
                        Vent
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Température (°C)</Label>
                <Input
                  type="number"
                  value={newIntervention.temperature}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, temperature: e.target.value }))}
                  placeholder="Ex: 18"
                />
              </div>
              <div className="space-y-2">
                <Label>Vent (km/h)</Label>
                <Input
                  type="number"
                  value={newIntervention.vent_kmh}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, vent_kmh: e.target.value }))}
                  placeholder="Ex: 10"
                />
                {newIntervention.vent_kmh && parseFloat(newIntervention.vent_kmh) > 19 && (
                  <p className="text-[10px] text-amber-600">⚠️ Vent fort - traitement déconseillé</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Hygrométrie (%)</Label>
                <Input
                  type="number"
                  value={newIntervention.hygrometrie}
                  onChange={(e) => setNewIntervention(prev => ({ ...prev, hygrometrie: e.target.value }))}
                  placeholder="Ex: 65"
                />
              </div>
            </div>
            
            {/* Observations */}
            <div className="space-y-2">
              <Label>Commentaire / Observations</Label>
              <Textarea
                value={newIntervention.observations}
                onChange={(e) => setNewIntervention(prev => ({ ...prev, observations: e.target.value }))}
                placeholder="Notes sur l'intervention..."
                rows={3}
                data-testid="input-commentaire"
              />
            </div>
            
            {/* Section Notifications & Signature - IDENTIQUE à InterventionsPage */}
            <div className="space-y-4 border-t pt-4">
              <Label className="text-lg font-semibold flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notifications & Signature
              </Label>
              
              {/* Notification opérateur */}
              <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                <Switch
                  id="notifier-operateur-parcellaire"
                  checked={newIntervention.notifier_operateur}
                  onCheckedChange={(checked) => setNewIntervention(prev => ({ ...prev, notifier_operateur: checked }))}
                  data-testid="switch-notifier-operateur"
                />
                <div className="space-y-1">
                  <Label htmlFor="notifier-operateur-parcellaire" className="cursor-pointer font-medium">
                    Notifier l'opérateur
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    L'opérateur recevra un email et une notification push avec les détails de l'intervention
                  </p>
                </div>
              </div>
              
              {/* Notification client */}
              <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Switch
                    id="notifier-client-parcellaire"
                    checked={newIntervention.notifier_client}
                    onCheckedChange={(checked) => setNewIntervention(prev => ({ ...prev, notifier_client: checked }))}
                    data-testid="switch-notifier-client"
                  />
                  <div className="space-y-1 flex-1">
                    <Label htmlFor="notifier-client-parcellaire" className="cursor-pointer font-medium">
                      Prévenir le client
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Envoyer un email au client pour l'informer de l'intervention planifiée
                    </p>
                  </div>
                </div>
                
                {newIntervention.notifier_client && (
                  <div className="ml-10 space-y-2">
                    <Label className="text-sm">Email du client</Label>
                    <Input
                      type="email"
                      value={newIntervention.client_email}
                      onChange={(e) => setNewIntervention(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="client@example.com"
                      data-testid="input-client-email"
                    />
                  </div>
                )}
              </div>
              
              {/* Signature électronique */}
              <div className="space-y-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-start space-x-3">
                  <Switch
                    id="demander-signature-parcellaire"
                    checked={newIntervention.demander_signature}
                    onCheckedChange={(checked) => setNewIntervention(prev => ({ ...prev, demander_signature: checked }))}
                    data-testid="switch-demander-signature"
                  />
                  <div className="space-y-1 flex-1">
                    <Label htmlFor="demander-signature-parcellaire" className="cursor-pointer font-medium flex items-center gap-2">
                      <FileSignature className="w-4 h-4 text-primary" />
                      Demander une signature de réception
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Obtenir une signature du client pour confirmer la réalisation du chantier
                    </p>
                  </div>
                </div>
                
                {newIntervention.demander_signature && (
                  <div className="ml-10 space-y-3">
                    <Label className="text-sm font-medium">Type de signature</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setNewIntervention(prev => ({ ...prev, type_signature: 'email' }))}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          newIntervention.type_signature === 'email'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-muted-foreground'
                        }`}
                        data-testid="signature-type-email"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-4 h-4" />
                          <span className="font-medium text-sm">Par email</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Le client reçoit un lien par email pour signer
                        </p>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => setNewIntervention(prev => ({ ...prev, type_signature: 'direct' }))}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          newIntervention.type_signature === 'direct'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-muted-foreground'
                        }`}
                        data-testid="signature-type-direct"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Smartphone className="w-4 h-4" />
                          <span className="font-medium text-sm">Sur appareil</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Signature directe sur téléphone ou tablette
                        </p>
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      L'opérateur pourra demander la signature via l'assistant
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowInterventionDialog(false);
              resetInterventionForm();
            }}>
              Annuler
            </Button>
            <Button onClick={createIntervention} disabled={!newIntervention.date_debut} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Créer l'intervention
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Panneau Itinéraire (latéral) */}
      {showItineraire && (
        <div className="fixed right-4 top-20 z-[2000] w-96 bg-background border rounded-xl shadow-2xl max-h-[80vh] overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-background">
            <h3 className="font-bold flex items-center gap-2">
              <Route className="w-5 h-5 text-orange-600" />
              Itinéraire agricole
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowItineraire(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Type de véhicule */}
            <div className="space-y-2">
              <Label className="text-xs">Type de véhicule</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'tracteur', label: '🚜 Tracteur', largeur: '3', hauteur: '3.5', poids: '15' },
                  { id: 'moissonneuse', label: '🌾 Moissonneuse', largeur: '6', hauteur: '4', poids: '25' },
                  { id: 'camion', label: '🚛 Camion', largeur: '2.6', hauteur: '4', poids: '44' },
                  { id: 'convoi', label: '🚚 Convoi', largeur: '4', hauteur: '4.5', poids: '80' },
                ].map(v => (
                  <Button
                    key={v.id}
                    type="button"
                    variant={itineraireData.typeVehicule === v.id ? "default" : "outline"}
                    size="sm"
                    className="text-xs"
                    onClick={() => setItineraireData(prev => ({
                      ...prev,
                      typeVehicule: v.id,
                      largeur: v.largeur,
                      hauteur: v.hauteur,
                      poids: v.poids
                    }))}
                  >
                    {v.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Dimensions */}
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Largeur (m)</Label>
                <Input
                  type="number"
                  step="0.1"
                  className="h-8 text-sm"
                  value={itineraireData.largeur}
                  onChange={(e) => setItineraireData(prev => ({ ...prev, largeur: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hauteur (m)</Label>
                <Input
                  type="number"
                  step="0.1"
                  className="h-8 text-sm"
                  value={itineraireData.hauteur}
                  onChange={(e) => setItineraireData(prev => ({ ...prev, hauteur: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Poids (t)</Label>
                <Input
                  type="number"
                  step="1"
                  className="h-8 text-sm"
                  value={itineraireData.poids}
                  onChange={(e) => setItineraireData(prev => ({ ...prev, poids: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Points A et B - Interface améliorée */}
            <div className="border-t pt-3 space-y-4">
              <h4 className="font-medium text-sm">📍 Définir les points</h4>
              
              {/* Point A - Départ */}
              <div className="space-y-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">A</div>
                  <span className="font-medium text-sm">Départ</span>
                  {itineraireData.pointA && (
                    <Badge variant="secondary" className="ml-auto text-xs">
                      ✅ {itineraireData.pointALabel || 'Défini'}
                    </Badge>
                  )}
                </div>
                
                {/* Recherche ville A */}
                <div className="relative">
                  <div className="flex gap-1">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher une ville..."
                        value={citySearchA}
                        onChange={(e) => setCitySearchA(e.target.value)}
                        className="h-8 text-xs pl-7"
                      />
                    </div>
                  </div>
                  
                  {/* Suggestions villes A */}
                  {citySuggestionsA.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                      {citySuggestionsA.map((city, idx) => (
                        <button
                          key={idx}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-xs border-b last:border-0"
                          onClick={() => selectPointFromSource('A', { lat: city.lat, lng: city.lng }, city.name)}
                        >
                          <div className="font-medium">{city.name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Sélection parcelle/exploitation A */}
                <div className="grid grid-cols-2 gap-1">
                  <Select onValueChange={(v) => {
                    const parcelle = parcelles.find(p => p.id === v);
                    if (parcelle?.center) {
                      selectPointFromSource('A', { lat: parcelle.center.lat, lng: parcelle.center.lng }, `🌾 ${parcelle.nom}`);
                    }
                  }}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Parcelle..." />
                    </SelectTrigger>
                    <SelectContent>
                      {parcelles.filter(p => p.center).map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-xs">{p.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select onValueChange={(v) => {
                    const expl = exploitations.find(e => e.id === v);
                    if (expl?.coordonnees) {
                      selectPointFromSource('A', { lat: expl.coordonnees.lat, lng: expl.coordonnees.lng }, `🏠 ${expl.nom}`);
                    } else if (expl) {
                      // Utiliser les coordonnées de la première parcelle de l'exploitation
                      const parcelleExpl = parcelles.find(p => p.exploitation_id === v && p.center);
                      if (parcelleExpl) {
                        selectPointFromSource('A', { lat: parcelleExpl.center.lat, lng: parcelleExpl.center.lng }, `🏠 ${expl.nom}`);
                      } else {
                        toast.error('Aucune coordonnée pour cette exploitation');
                      }
                    }
                  }}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Exploitation..." />
                    </SelectTrigger>
                    <SelectContent>
                      {exploitations.map(e => (
                        <SelectItem key={e.id} value={e.id} className="text-xs">{e.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Clic sur carte A */}
                <Button
                  type="button"
                  variant={selectingPoint === 'A' ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "w-full justify-center text-xs h-7",
                    selectingPoint === 'A' && "ring-2 ring-green-500 ring-offset-1 animate-pulse"
                  )}
                  onClick={() => {
                    setSelectingPoint(selectingPoint === 'A' ? null : 'A');
                    if (selectingPoint !== 'A') toast.info('👆 Cliquez sur la carte');
                  }}
                >
                  <MapPin className="w-3 h-3 mr-1" />
                  {selectingPoint === 'A' ? 'Cliquez sur la carte...' : 'Cliquer sur carte'}
                </Button>
              </div>
              
              {/* Point B - Arrivée */}
              <div className="space-y-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">B</div>
                  <span className="font-medium text-sm">Arrivée</span>
                  {itineraireData.pointB && (
                    <Badge variant="secondary" className="ml-auto text-xs">
                      ✅ {itineraireData.pointBLabel || 'Défini'}
                    </Badge>
                  )}
                </div>
                
                {/* Recherche ville B */}
                <div className="relative">
                  <div className="flex gap-1">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher une ville..."
                        value={citySearchB}
                        onChange={(e) => setCitySearchB(e.target.value)}
                        className="h-8 text-xs pl-7"
                      />
                    </div>
                  </div>
                  
                  {/* Suggestions villes B */}
                  {citySuggestionsB.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                      {citySuggestionsB.map((city, idx) => (
                        <button
                          key={idx}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-xs border-b last:border-0"
                          onClick={() => selectPointFromSource('B', { lat: city.lat, lng: city.lng }, city.name)}
                        >
                          <div className="font-medium">{city.name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Sélection parcelle/exploitation B */}
                <div className="grid grid-cols-2 gap-1">
                  <Select onValueChange={(v) => {
                    const parcelle = parcelles.find(p => p.id === v);
                    if (parcelle?.center) {
                      selectPointFromSource('B', { lat: parcelle.center.lat, lng: parcelle.center.lng }, `🌾 ${parcelle.nom}`);
                    }
                  }}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Parcelle..." />
                    </SelectTrigger>
                    <SelectContent>
                      {parcelles.filter(p => p.center).map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-xs">{p.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select onValueChange={(v) => {
                    const expl = exploitations.find(e => e.id === v);
                    if (expl?.coordonnees) {
                      selectPointFromSource('B', { lat: expl.coordonnees.lat, lng: expl.coordonnees.lng }, `🏠 ${expl.nom}`);
                    } else if (expl) {
                      const parcelleExpl = parcelles.find(p => p.exploitation_id === v && p.center);
                      if (parcelleExpl) {
                        selectPointFromSource('B', { lat: parcelleExpl.center.lat, lng: parcelleExpl.center.lng }, `🏠 ${expl.nom}`);
                      } else {
                        toast.error('Aucune coordonnée pour cette exploitation');
                      }
                    }
                  }}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Exploitation..." />
                    </SelectTrigger>
                    <SelectContent>
                      {exploitations.map(e => (
                        <SelectItem key={e.id} value={e.id} className="text-xs">{e.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Clic sur carte B */}
                <Button
                  type="button"
                  variant={selectingPoint === 'B' ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "w-full justify-center text-xs h-7",
                    selectingPoint === 'B' && "ring-2 ring-red-500 ring-offset-1 animate-pulse"
                  )}
                  onClick={() => {
                    setSelectingPoint(selectingPoint === 'B' ? null : 'B');
                    if (selectingPoint !== 'B') toast.info('👆 Cliquez sur la carte');
                  }}
                >
                  <MapPin className="w-3 h-3 mr-1" />
                  {selectingPoint === 'B' ? 'Cliquez sur la carte...' : 'Cliquer sur carte'}
                </Button>
              </div>
            </div>
            
            {/* Bouton CALCULER L'ITINÉRAIRE - Principal */}
            <Button
              disabled={!itineraireData.pointA || !itineraireData.pointB || calculatingRoute}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3"
              onClick={calculateHGVRoute}
            >
              {calculatingRoute ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <Route className="w-5 h-5 mr-2" />
                  Calculer l'itinéraire Poids Lourds
                </>
              )}
            </Button>
            
            {/* Résultat de l'itinéraire calculé */}
            {routeInfo && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-blue-800 dark:text-blue-200">Itinéraire calculé</span>
                  <Badge className="bg-blue-600">{itineraireData.typeVehicule}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-blue-600" />
                    <span className="font-bold text-lg">{routeInfo.distance} km</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-bold text-lg">{routeInfo.duration} min</span>
                  </div>
                </div>
                {routeInfo.warnings && routeInfo.warnings.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                    <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                      {routeInfo.warnings.map((w, i) => (
                        <li key={i}>⚠️ {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {/* Restrictions */}
            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-xs">
              <p className="font-medium text-orange-800 dark:text-orange-200">Restrictions réglementaires :</p>
              <ul className="mt-1 text-orange-700 dark:text-orange-300 space-y-0.5">
                {parseFloat(itineraireData.largeur) > 3 && <li>• Largeur {"> 3m"} : Signalisation</li>}
                {parseFloat(itineraireData.largeur) > 4 && <li>• Largeur {"> 4m"} : Escorte</li>}
                {parseFloat(itineraireData.hauteur) > 4 && <li>• Hauteur {"> 4m"} : Ponts</li>}
                {parseFloat(itineraireData.poids) > 44 && <li>• Poids {"> 44t"} : Autorisation</li>}
                {parseFloat(itineraireData.largeur) <= 3 && parseFloat(itineraireData.hauteur) <= 4 && parseFloat(itineraireData.poids) <= 44 && (
                  <li className="text-green-600">✅ OK - Circulation normale</li>
                )}
              </ul>
            </div>
            
            {/* Boutons navigation externe */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Ouvrir dans une app GPS :</p>
              
              {/* HERE WeGo - Poids Lourds */}
              <Button
                disabled={!itineraireData.pointA || !itineraireData.pointB}
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  const url = `https://wego.here.com/directions/drive/${itineraireData.pointALabel || 'Départ'},${itineraireData.pointA.lat},${itineraireData.pointA.lng}/${itineraireData.pointBLabel || 'Arrivée'},${itineraireData.pointB.lat},${itineraireData.pointB.lng}?map=${itineraireData.pointA.lat},${itineraireData.pointA.lng},12`;
                  window.open(url, '_blank');
                  toast.info('HERE WeGo ouvert - Activez le mode Camion', { duration: 5000 });
                }}
              >
                <Truck className="w-4 h-4 mr-2" />
                HERE WeGo
              </Button>
              
              {/* Google Maps */}
              <Button
                disabled={!itineraireData.pointA || !itineraireData.pointB}
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  const url = `https://www.google.com/maps/dir/${itineraireData.pointA.lat},${itineraireData.pointA.lng}/${itineraireData.pointB.lat},${itineraireData.pointB.lng}`;
                  window.open(url, '_blank');
                }}
              >
                <Route className="w-4 h-4 mr-2" />
                Google Maps
              </Button>
              
              {/* Copier coordonnées */}
              <Button
                disabled={!itineraireData.pointA || !itineraireData.pointB}
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => {
                  const coords = `Départ: ${itineraireData.pointA.lat.toFixed(6)}, ${itineraireData.pointA.lng.toFixed(6)}\nArrivée: ${itineraireData.pointB.lat.toFixed(6)}, ${itineraireData.pointB.lng.toFixed(6)}\nDimensions: L${itineraireData.largeur}m x H${itineraireData.hauteur}m x ${itineraireData.poids}t`;
                  navigator.clipboard.writeText(coords);
                  toast.success('Coordonnées copiées');
                }}
              >
                📋 Copier pour GPS externe
              </Button>
            </div>
            
            {/* Bouton effacer */}
            {(itineraireData.pointA || itineraireData.pointB || calculatedRoute) && (
              <Button
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  setItineraireData(prev => ({
                    ...prev,
                    pointA: null,
                    pointALabel: '',
                    pointB: null,
                    pointBLabel: ''
                  }));
                  setSelectingPoint(null);
                  setCalculatedRoute(null);
                  setRouteInfo(null);
                  toast.info('Itinéraire effacé');
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Effacer l'itinéraire
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Calculette produit - IDENTIQUE à InterventionsPage */}
      {showCalculette && (
        <CalculetteProduit 
          produit={showCalculette.produit}
          volumeBouillie={showCalculette.volumeBouillie}
          onClose={() => setShowCalculette(null)}
        />
      )}
      
      {/* Dialog Alertes IA avant intervention */}
      <AlertesParcelle
        parcelle={parcelleForIntervention}
        parcelleId={parcelleForIntervention?.id}
        typeIntervention={newIntervention?.type_intervention}
        isOpen={showAlertesDialog}
        onClose={() => setShowAlertesDialog(false)}
        onContinue={handleContinueAfterAlertes}
      />
      
      {/* FAB Mobile - Boutons flottants actions rapides */}
      <MobileFloatingActions />

      {/* Dialog création bâtiment */}
      <Dialog open={showCreateBatiment} onOpenChange={setShowCreateBatiment}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter un bâtiment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nom</label>
              <Input
                placeholder="Ex: Hangar principal"
                value={newBatiment.nom}
                onChange={e => setNewBatiment(prev => ({ ...prev, nom: e.target.value }))}
                data-testid="batiment-nom-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select 
                value={newBatiment.type_batiment} 
                onValueChange={v => setNewBatiment(prev => ({ ...prev, type_batiment: v }))}
              >
                <SelectTrigger data-testid="batiment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[
                    { v: 'hangar', l: '🏗️ Hangar' },
                    { v: 'atelier', l: '🔧 Atelier' },
                    { v: 'bureaux', l: '🏢 Bureaux' },
                    { v: 'distillerie', l: '🔥 Distillerie' },
                    { v: 'cave', l: '🍷 Cave' },
                    { v: 'serre', l: '🌿 Serre' },
                    { v: 'silo', l: '🏭 Silo' },
                    { v: 'entrepot', l: '📦 Entrepôt' },
                    { v: 'garage', l: '🚜 Garage' },
                    { v: 'chai', l: '🍇 Chai' },
                    { v: 'autre', l: '📍 Autre' },
                  ].map(t => (
                    <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                placeholder="Description optionnelle"
                value={newBatiment.description}
                onChange={e => setNewBatiment(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Entreprise</label>
                <Input
                  placeholder="Nom entreprise"
                  value={newBatiment.nom_entreprise}
                  onChange={e => setNewBatiment(prev => ({ ...prev, nom_entreprise: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Surface (m²)</label>
                <Input
                  type="number"
                  placeholder="Surface"
                  value={newBatiment.surface_m2}
                  onChange={e => setNewBatiment(prev => ({ ...prev, surface_m2: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newBatiment.accessible_a_tous}
                onChange={e => setNewBatiment(prev => ({ ...prev, accessible_a_tous: e.target.checked }))}
                id="accessible_check"
              />
              <label htmlFor="accessible_check" className="text-sm">Accessible à tous les workspaces</label>
            </div>
            <div className="text-xs text-muted-foreground">
              Position : {newBatiment.latitude?.toFixed(6)}, {newBatiment.longitude?.toFixed(6)}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowCreateBatiment(false)}>Annuler</Button>
            <Button 
              data-testid="btn-save-batiment"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={async () => {
                if (!newBatiment.nom) { toast.error('Nom requis'); return; }
                try {
                  await api.post('/parcellaire/batiments', {
                    ...newBatiment,
                    surface_m2: newBatiment.surface_m2 ? parseFloat(newBatiment.surface_m2) : null
                  });
                  toast.success('Bâtiment ajouté !');
                  setShowCreateBatiment(false);
                  setNewBatiment({ nom: '', type_batiment: 'hangar', description: '', latitude: null, longitude: null, nom_entreprise: '', accessible_a_tous: true, surface_m2: '' });
                  fetchData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Erreur');
                }
              }}
            >Créer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bandeau mode placement */}
      {placingBatiment && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-amber-100 border border-amber-400 px-6 py-3 rounded-xl shadow-lg z-[1000] flex items-center gap-3">
          <span className="text-amber-800 font-medium">Cliquez sur la carte pour placer le bâtiment</span>
          <Button size="sm" variant="outline" onClick={() => setPlacingBatiment(false)}>Annuler</Button>
        </div>
      )}
    </div>
  );
};

export default ParcellairePage;
